/**
 * Todo rótulo visível ao usuário mora aqui.
 *
 * Duas razões. A primeira: o público inclui fiscais de contrato e servidores que não
 * são de TI — "RDM", "rollback" e "CAB" precisam de tradução e de ajuda contextual.
 * A segunda: renomear um termo na interface passa a ser mudar uma string, nunca mexer
 * em rota, tipo, tabela ou nome de campo da API.
 */

export const TERMOS = {
  gmud: {
    rotulo: 'Solicitação de Mudança',
    /**
     * O plural existe como entrada própria porque a listagem é o título de uma tela que mostra
     * muitas, e "Solicitação de Mudança" sobre uma tabela de 1.027 linhas se lê como erro. Não é
     * `rotulo + 's'`: em pt-BR a flexão cai no primeiro termo, não no fim da expressão.
     */
    rotuloPlural: 'Solicitações de Mudança',
    sigla: 'GMUD',
    ajuda:
      'Registro formal de qualquer alteração que vá para produção. Pelo contrato, nada entra em produção sem passar por aqui.',
  },
  cab: {
    rotulo: 'Comitê de Mudanças',
    sigla: 'CAB',
    ajuda:
      'Grupo da equipe de TI da Prefeitura que avalia e aprova as mudanças antes da implantação. Reúne-se às terças e quintas.',
  },
  ecab: {
    rotulo: 'Comitê Emergencial',
    sigla: 'eCAB',
    ajuda: 'Reunião extraordinária do comitê, convocada fora da janela normal para tratar de mudança urgente.',
  },
  planoRollback: {
    rotulo: 'Plano de volta',
    ajuda: 'Como desfazer a mudança e voltar ao estado anterior, caso algo dê errado na implantação.',
  },
  planoContingencia: {
    rotulo: 'Plano de contingência',
    ajuda: 'O que fazer para manter o serviço funcionando enquanto o problema não é resolvido.',
  },
  evidencia: {
    rotulo: 'Comprovação',
    ajuda:
      'Arquivo ou link que comprova o que foi feito: relatório de teste, log, cobertura de código, print do painel, homologação.',
  },
  janelaImplantacao: {
    rotulo: 'Janela de implantação',
    ajuda: 'Horário em que mudanças podem ir para produção: terças e quintas, a partir das 17h.',
  },
  iad: {
    rotulo: 'Pontos administrativos',
    sigla: 'IAD',
    ajuda:
      'Pontuação prevista no contrato para ocorrências administrativas. Implantar em produção sem aprovação do comitê vale 10 pontos.',
  },
  ocorrenciaAdministrativa: {
    rotulo: 'Ocorrência administrativa',
    ajuda:
      'Um dos 27 descumprimentos listados no Anexo VIII do contrato, cada um com pontuação fixa. Os pontos do ' +
      'mês são somados e a soma cai numa faixa: até 10 pontos não há desconto; de 11 a 20, 5% da nota; de 21 ' +
      'a 30, 7%; acima de 30, 10%.',
  },
  conciliacaoDoItem16: {
    rotulo: 'Conciliação do item 16',
    ajuda:
      'Solicitações de mudança que chegaram à execução sem que o sistema tenha registro da aprovação do ' +
      'comitê. O sistema apenas aponta: nenhum ponto é lançado sem alguém confirmar, e o histórico importado ' +
      'do sistema anterior nunca entra nesta lista.',
  },
  trp: {
    rotulo: 'Termo de recebimento provisório',
    sigla: 'TRP',
    ajuda:
      'O documento que a fiscalização assina ao fechar o mês. Registra o que foi executado, os seis ' +
      'indicadores apurados e a redução que eles impõem sobre a nota fiscal do serviço.',
  },
  serieDeIndicadores: {
    rotulo: 'Série histórica dos indicadores',
    ajuda:
      'Os seis indicadores mês a mês. O contrato prevê advertência quando um indicador fica abaixo do ' +
      'limite e multa quando isso se repete — e repetição não se enxerga olhando um mês por vez.',
  },
  conferenciaDoMes: {
    rotulo: 'Conferência do mês',
    ajuda:
      'A declaração de que você varreu as ocorrências administrativas do mês. Sem ela o indicador fica sem ' +
      'número, porque "nenhuma ocorrência" e "ninguém olhou" apareceriam iguais na tela. Conferir não fecha ' +
      'nada: ocorrência descoberta depois continua entrando, e a soma muda junto.',
  },
  rdm: {
    rotulo: 'Envio ao comitê',
    sigla: 'RDM',
    ajuda:
      'Requisição de Mudança: o ato de enviar a solicitação já preenchida para o comitê avaliar. Enquanto não for enviada, ela fica em rascunho e só você a vê.',
  },
  risco: {
    rotulo: 'Risco',
    ajuda:
      'Faixa calculada a partir da probabilidade de algo dar errado e do impacto se der. Fica em branco enquanto o comitê não avalia.',
  },
  bloqueadoParaDeploy: {
    rotulo: 'Liberação para produção',
    ajuda: 'Enquanto estiver bloqueada, esta mudança não pode ir para produção. Só a aprovação do comitê libera.',
  },
  historicoMigrado: {
    rotulo: 'Histórico do sistema anterior',
    ajuda:
      'As 1.027 solicitações que já existiam no sistema antigo foram trazidas para cá para consulta e pesquisa. ' +
      'Elas não passaram pelo processo deste sistema: não há decisão de comitê registrada, nem comprovação de ' +
      'teste, nem conferência contra o contrato. Por isso ficam marcadas como histórico e não entram como ' +
      'aprovadas em relatório nem em indicador.',
  },

  // ------------------------------------------------------------------
  // Chamados e fiscalização do contrato SSTD 01/2026
  // ------------------------------------------------------------------

  chamado: {
    rotulo: 'Chamado',
    /** Mesma razão do plural de GMUD: em pt-BR a flexão não é o rótulo mais "s" em toda expressão. */
    rotuloPlural: 'Chamados',
    ajuda:
      'Atendimento aberto na Central de Atendimento e-Niterói. É por ele que toda demanda de sustentação entra ' +
      'no contrato, e é sobre ele que a fiscalização de cada mês é feita.',
  },
  sla: {
    rotulo: 'Prazo combinado',
    sigla: 'SLA',
    ajuda:
      'O tempo que o contrato dá para o chamado ser atendido, contado em horas úteis. São dois prazos: o de dar ' +
      'a primeira resposta e o de resolver por completo.',
  },
  semPrazoDeSla: {
    rotulo: 'Sem prazo definido',
    ajuda:
      'Este chamado não tem prazo de solução registrado, então não existe atraso a medir nele. Isso NÃO quer ' +
      'dizer que foi atendido no prazo: quer dizer que ele fica fora da base de medição. O contrato prevê o ' +
      'caso — serviço agendado tem prazo para responder, não para concluir.',
  },
  competencia: {
    rotulo: 'Mês de referência',
    ajuda:
      'O mês em que o chamado foi FECHADO, e não o mês em que foi aberto. É o critério que a fiscalização de ' +
      'maio/2026 usou para calcular os indicadores; trocá-lo mudaria todos os números de uma vez. Quando a ' +
      'planilha marca um chamado como "do mês", vale a marcação do fiscal: em maio isso vale para 3 chamados ' +
      'que fecharam em junho e que o termo de recebimento assinado conta em maio.',
  },
  origemDaDemanda: {
    rotulo: 'Como a demanda entrou',
    ajuda:
      'Sustentação é a via normal do e-Niterói. Projeto é a solicitação de melhoria que o contrato admite como ' +
      'entrada de projeto — ela conta para a conformidade e para o aceite, mas fica fora do indicador de prazos ' +
      'da sustentação. Esta separação ainda depende de confirmação do gestor do contrato.',
  },
  procedenciaDoChamado: {
    rotulo: 'De onde veio este registro',
    ajuda:
      'Um chamado pode ter vindo da planilha entregue pela fiscalização ou da leitura direta do e-Niterói. As ' +
      'duas fontes podem discordar sobre o mesmo chamado, cada uma certa no seu instante — por isso a origem ' +
      'aparece em toda linha, e um dado nunca sobrescreve o outro em silêncio.',
  },
  fechamentoDoMes: {
    rotulo: 'Fechamento do mês',
    ajuda:
      'A apuração dos indicadores de um mês de referência, com o número, a conta que o produziu e a lista de ' +
      'chamados que entraram nela. É esta tela que sustenta o termo de recebimento assinado pela fiscalização.',
  },
  redutorDaFatura: {
    rotulo: 'Desconto na nota do mês',
    ajuda:
      'Os descontos de cada indicador SOMAM, e a soma tem limite de 10% sobre a nota fiscal do mês (Anexo VIII, ' +
      'item h, do Termo de Referência). Quando a soma passa de 10%, o que vale é o limite — não o valor somado.',
  },
  ica: {
    rotulo: 'Conformidade dos artefatos',
    sigla: 'ICA',
    ajuda:
      'Mede quantas entregas do mês vieram fora do padrão acordado (faltou anexo, relatório fora do modelo). ' +
      'Quanto maior, melhor: 1,00 é nenhuma entrega com problema.',
  },
  iap: {
    rotulo: 'Atendimento aos prazos',
    sigla: 'IAP',
    ajuda:
      'Mede quantos chamados foram atendidos dentro do prazo combinado. As manutenções corretivas ficam fora ' +
      'desta conta: elas têm indicador próprio de tempo de solução.',
  },
  iae: {
    rotulo: 'Aceitação de entrega',
    sigla: 'IAE',
    ajuda:
      'Mede se o usuário aceitou o serviço como concluído. O aceite parcial conta como um terço do integral, ' +
      'e a entrega recusada continua no total sem somar nada.',
  },
  tis: {
    rotulo: 'Tempo de solução de incidentes',
    sigla: 'TIS',
    ajuda:
      'Mede se as manutenções corretivas foram resolvidas dentro do prazo da sua criticidade: 12 horas úteis na ' +
      'alta, 24 na média, 40 na baixa.',
  },
  iie: {
    rotulo: 'Infraestrutura',
    sigla: 'IIE',
    ajuda:
      'Mede backup, restauração, disponibilidade e comunicação de incidentes. Três desses números nascem ' +
      'nas ferramentas da contratada; a disponibilidade a Prefeitura também mede com sonda própria. Quem lê ' +
      'e registra os números, no fechamento do mês, é a fiscalização.',
  },
  cargaDaPlanilha: {
    rotulo: 'Carga da planilha',
    ajuda:
      'O envio da planilha de chamados do mês para dentro do sistema. É a etapa "b" do termo de recebimento ' +
      'assinado pela fiscalização e continua sendo o documento oficial. A atualização automática do ' +
      'e-Niterói complementa a planilha durante o mês, mas não a substitui.',
  },

  ordemDeServico: {
    rotulo: 'Ordem de serviço',
    rotuloPlural: 'Ordens de serviço',
    sigla: 'OS',
    ajuda:
      'O documento pelo qual a Prefeitura pede formalmente um serviço à contratada. Sem ele, o ' +
      'contrato não autoriza executar nem cobrar — e incluir na cobrança um serviço que nenhuma ordem ' +
      'pediu é infração prevista no contrato.',
  },
  regimeDaOs: {
    rotulo: 'Modelo da ordem',
    ajuda:
      'São dois. O mensal cobre sustentação e infraestrutura, e o contrato manda emitir um no início ' +
      'de cada mês. O de projeto é um por projeto, medido em Pontos de Função, e atravessa meses — ' +
      'por isso ele não pertence a um mês de fiscalização.',
  },
  entregaAoPreposto: {
    rotulo: 'Entrega ao preposto',
    ajuda:
      'A entrega formal da ordem ao representante da contratada. É do dia útil seguinte a ela que o ' +
      'prazo de execução começa a correr. Sem esse registro, nenhum prazo da ordem pode ser apurado.',
  },
  termoDeEncerramentoDaOs: {
    rotulo: 'Termo de encerramento da ordem',
    ajuda:
      'O documento que encerra a ordem de serviço. É dele que o contrato conta o prazo do recebimento ' +
      'provisório — mas o contrato não traz o modelo dele e não diz quem o emite. Por isso o sistema ' +
      'guarda a data em que ele chegou e onde ele está, e não o documento.',
  },
  quantidadeComoFracao: {
    rotulo: 'Quantidade',
    ajuda:
      'Nos meses em que o serviço não durou o mês inteiro, a quantidade é uma fração: 25/30 quer dizer ' +
      '25 dias de um mês contado sempre como 30. É assim que o termo de recebimento a escreve, e é ' +
      'assim que o sistema a guarda.',
  },
  divisaoDaOs: {
    rotulo: 'Divisão por órgão',
    ajuda:
      'O modelo do contrato tem uma seção que reparte o valor da ordem entre secretarias. O que essa ' +
      'seção decide — informação de custo, cobrança a outro órgão, ou outra coisa — não está definido ' +
      'em lugar nenhum do contrato, e nenhum documento do contrato a preencheu até hoje.',
  },
  fiscalRequisitante: {
    rotulo: 'Fiscal requisitante',
    ajuda:
      'Quem assina a ordem de serviço ao lado do gestor do contrato. O contrato cita esse papel nos ' +
      'modelos de ordem e não o inclui na equipe de fiscalização — a atribuição ainda depende de definição.',
  },
  imr: {
    rotulo: 'Indicadores de medição de resultado',
    sigla: 'IMR',
    ajuda:
      'Os seis indicadores do Anexo VIII do contrato — conformidade, prazo, aceite, tempo de solução, ' +
      'pontos administrativos e infraestrutura — apurados todo mês. É a série deles, mês a mês, que ' +
      'mostra se o serviço está melhorando ou piorando.',
  },
  atividadeNosRepositorios: {
    rotulo: 'Atividade nos repositórios',
    ajuda:
      'O que entrou na branch principal dos repositórios do e-Cidade, mês a mês: quem fez o quê, por ' +
      'setor e por pessoa. É evidência para a fiscalização e para o comitê — nada aqui muda solicitação, ' +
      'chamado ou apuração.',
  },
  branchPrincipal: {
    rotulo: 'Branch principal',
    ajuda:
      'A linha principal do código no repositório (a "main"). O que está nela é o que a próxima ' +
      'implantação leva para produção — por isso é ela que a fiscalização acompanha.',
  },
  setorDoAutor: {
    rotulo: 'Setor',
    ajuda:
      'De onde vem quem fez o commit, deduzido do domínio do e-mail: contratada (DBSeller), Secretaria ' +
      'Municipal de Fazenda, SEXEC/SSTD, outro órgão da Prefeitura, e-mail pessoal ou robô. O fiscal ' +
      'pode corrigir, e a correção vale daí em diante.',
  },
  integracao: {
    rotulo: 'Integração',
    ajuda:
      'Commit de merge: junta uma branch à principal. Diz quem integrou e quando; não é trabalho de ' +
      'autoria, e por isso não entra na conta de commits de cada pessoa.',
  },
  equipeInterna: {
    rotulo: 'Equipe interna',
    ajuda:
      'Quem está cadastrado no Diário de Bordo — a equipe da SEXEC. Por decisão do dono em 07/09/2026, ' +
      'os commits dessas pessoas ficam fora dos relatórios: o que se mede aqui é o trabalho da contratada e ' +
      'dos outros órgãos. O casamento com o Diário é automático — pelo login do GitHub, pelo e-mail, pelo ' +
      'e-mail alternativo, pela caixa postal ou pelo nome — e o fiscal pode corrigir quando ele errar.',
  },
  areaDoCodigo: {
    rotulo: 'Área do código',
    ajuda:
      'Um agrupamento pelo caminho do arquivo: as duas primeiras pastas, ou o prefixo do nome quando o ' +
      'arquivo está na raiz. Não é um módulo do sistema — serve para ver ONDE se mexeu no e-Cidade, sem ' +
      'precisar ler arquivo por arquivo.',
  },
} as const

/** Estados da solicitação, na linguagem de quem usa. */
export const ROTULO_STATUS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  SOLICITADA: 'Aguardando pauta',
  EM_AVALIACAO_CAB: 'Em avaliação pelo comitê',
  AGUARDANDO_AJUSTES: 'Aguardando ajustes',
  APROVADA: 'Aprovada',
  RECUSADA: 'Recusada',
  EM_EXECUCAO: 'Em execução',
  EM_VERIFICACAO: 'Em verificação',
  CONCLUIDA: 'Concluída',
  REVERTIDA_ROLLBACK: 'Revertida',
  // "Cancelada" e "Recusada" não são a mesma coisa e a interface não pode deixar parecer que são:
  // recusa é decisão do comitê sobre o mérito; cancelamento é desistência de quem pediu.
  CANCELADA: 'Cancelada',
  // Não é etapa do processo: é o estado terminal em que o histórico importado entrou, sem
  // saída na matriz de transições. O rótulo é o mesmo de `ref_status_gmud` (migração V10),
  // e diz o que a coisa é em vez de dizer em que pé ela está.
  MIGRADO_V1: 'Histórico (sistema anterior)',
}

/**
 * De onde o registro veio.
 *
 * Existe para a interface nunca ter de escrever "MIGRADO_V1" na cara de ninguém, e para a
 * distinção entre histórico e processo novo ser a mesma palavra em toda tela — lista,
 * detalhe, painel e relatório.
 */
export const ROTULO_ORIGEM: Record<string, string> = {
  V2: 'Registro deste sistema',
  MIGRADO_V1: 'Histórico do sistema anterior',
}

export const ROTULO_PRIORIDADE: Record<string, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
  EMERGENCIAL: 'Emergencial',
}

/** As 7 etapas do ANEXO XXI do Termo de Referência, na ordem em que ocorrem. */
export const ROTULO_ETAPA: Record<string, string> = {
  PLANEJAMENTO: '1. Planejamento da mudança',
  SOLICITACAO_FORMAL: '2. Solicitação formal',
  AVALIACAO_CAB: '3. Avaliação pelo comitê',
  APROVACAO_PLANO: '4. Aprovação do plano',
  EXECUCAO: '5. Execução',
  VERIFICACAO_VALIDACAO: '6. Verificação e validação',
  FECHAMENTO: '7. Fechamento',
}

/** De onde a mudança nasceu. */
export const ROTULO_TIPO: Record<string, string> = {
  PROJETO: 'Projeto',
  CHAMADO: 'Chamado',
}

/** As três causas do item 8.31.5.2 do TR. Só valem quando a prioridade é emergencial. */
export const ROTULO_CAUSA_EMERGENCIAL: Record<string, string> = {
  ATENDER_NEGOCIO: 'Atender necessidade do negócio',
  PROBLEMA_SOFTWARE: 'Corrigir problema do software',
  ERRO_IMPLEMENTACAO: 'Corrigir erro de implantação anterior',
}

/**
 * Se a mudança resolve o problema de vez ou o contorna por enquanto (V54, pedido do Teste de uso 1).
 *
 * Curto aqui, porque é o que a conferência, o detalhe e o RDM imprimem. No formulário cada opção
 * vem com o efeito escrito ao lado (`NATUREZA_DA_SOLUCAO.opcoes`): "paliativa" sozinha não diz a
 * quem é leigo que o problema continua lá.
 */
export const ROTULO_NATUREZA_DA_SOLUCAO: Record<string, string> = {
  DEFINITIVA: 'Definitiva',
  PALIATIVA: 'Paliativa',
}

/** A pergunta da natureza da solução, como o formulário a faz e as telas de leitura a rotulam. */
export const NATUREZA_DA_SOLUCAO = {
  rotulo: 'Solução',
  pergunta: 'A solução é definitiva ou paliativa?',
  ajuda:
    'Paliativa contorna o problema por enquanto. O contrato não aceita paliativo deixado como solução ' +
    'permanente (Anexo VIII, item 14).',
  opcoes: {
    DEFINITIVA: 'Definitiva — resolve a causa do problema',
    PALIATIVA: 'Paliativa — contorna o problema por enquanto',
  },
} as const

/** Faixas da coluna calculada `risco_nivel`. */
export const ROTULO_RISCO: Record<string, string> = {
  BAIXO: 'Baixo',
  MODERADO: 'Moderado',
  ALTO: 'Alto',
  CRITICO: 'Crítico',
}

/**
 * Situação do chamado diante do prazo.
 *
 * "Sem prazo definido" é escrito assim, e não "—" nem "Sem SLA": travessão numa coluna de situação
 * se lê como dado faltando, e a sigla não diz nada a quem não é de TI. São 12 dos 64 chamados
 * medidos no e-Niterói e 7 dos 70 da planilha de maio — não é exceção rara, é um sétimo da lista.
 */
export const ROTULO_SITUACAO_SLA: Record<string, string> = {
  DENTRO_DO_PRAZO: 'Dentro do prazo',
  ESTOURADO: 'Prazo estourado',
  SEM_PRAZO: 'Sem prazo definido',
}

/** Por qual porta a demanda entrou no contrato. */
export const ROTULO_ORIGEM_DEMANDA: Record<string, string> = {
  SUSTENTACAO: 'Sustentação',
  PROJETO: 'Projeto',
}

/**
 * De onde o registro do chamado veio.
 *
 * O servidor chama a segunda de "e-Niterói (API)". Aqui ela é "leitura automática" pela Regra nº 4:
 * o público inclui fiscal de contrato, e "API" é jargão. O vínculo com o contrato de dados continua
 * sendo o valor do enum, que não muda por causa de rótulo.
 */
export const ROTULO_ORIGEM_CHAMADO: Record<string, string> = {
  PLANILHA_FISCALIZACAO: 'Planilha da fiscalização',
  GLPI_REST: 'e-Niterói (leitura automática)',
}

/** A mesma procedência na coluna da tabela, onde o rótulo inteiro cortava. O completo vai no `title`. */
export const ROTULO_CURTO_ORIGEM_CHAMADO: Record<string, string> = {
  PLANILHA_FISCALIZACAO: 'Planilha',
  GLPI_REST: 'e-Niterói',
}

/** Criticidade que define o prazo do tempo de solução (12h, 24h e 40h úteis). */
export const ROTULO_CRITICIDADE_TIS: Record<string, string> = {
  ALTA: 'Alta',
  MEDIA: 'Média',
  BAIXA: 'Baixa',
}

/**
 * O que uma carga fez com cada chamado.
 *
 * Os três em voz de resultado, e não "NOVO/ALTERADO/SEM_MUDANCA": quem lê o histórico quer saber o
 * que aconteceu com o registro, não em que categoria o importador o classificou.
 */
export const ROTULO_EFEITO_DA_CARGA: Record<string, string> = {
  NOVO: 'Entrou nesta carga',
  ALTERADO: 'Foi atualizado nesta carga',
  SEM_MUDANCA: 'Veio igual ao que já havia',
}

/** Em que pé está uma importação. `FALHOU` existe para carga interrompida deixar rastro. */
export const ROTULO_SITUACAO_DA_CARGA: Record<string, string> = {
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
  FALHOU: 'Falhou',
}

/**
 * O que cada indicador do ANEXO VIII mede, e onde conferir no contrato.
 *
 * `meta` é o alvo escrito no TR. `semDesconto` é outra coisa, e a diferença não é detalhe: no IAE a
 * meta do contrato é 75%, mas a tabela de desconto põe "75% a 89%" na faixa de 3% — quem cumpre a
 * meta exata ainda leva glosa. Exibir só a meta ao lado de um desconto de 3% pareceria erro do
 * sistema; exibir os dois números mostra que é o contrato que diz isso. Os textos são conferíveis
 * contra as páginas indicadas.
 */
export interface AjudaDeIndicador {
  meta: string
  semDesconto: string
  formula: string
  ondeNoTr: string
}

export const AJUDA_DO_INDICADOR: Record<string, AjudaDeIndicador> = {
  ICA: {
    meta: '0,95 ou mais',
    semDesconto: '0,95 ou acima',
    formula: '1 − (entregas com inconformidade ÷ entregas do mês)',
    ondeNoTr: 'Anexo VIII, p. 150-151',
  },
  IAP: {
    meta: '90% ou mais',
    semDesconto: 'Igual ou acima de 90%',
    formula: '100 × (chamados atendidos no prazo ÷ chamados não corretivos do mês)',
    ondeNoTr: 'Anexo VIII, p. 152-153',
  },
  IAE: {
    meta: '75% ou mais',
    semDesconto: '90% ou acima',
    formula: '((aceites integrais + aceites parciais ÷ 3) × 100) ÷ entregas do mês',
    ondeNoTr: 'Anexo VIII, p. 154-155',
  },
  IAD: {
    meta: 'até 10 pontos',
    semDesconto: 'de 0 a 10 pontos',
    formula: 'soma dos pontos das ocorrências administrativas do mês',
    ondeNoTr: 'Anexo VIII, p. 157-159',
  },
  TIS: {
    meta: 'dentro do prazo da criticidade',
    semDesconto: 'nenhuma correção fora do prazo',
    formula: 'atraso da manutenção corretiva, por faixa de criticidade',
    ondeNoTr: 'Anexo VIII, p. 160-162',
  },
  IIE: {
    meta: '95% de backup e 90% de disponibilidade',
    semDesconto: 'metas de backup, restauração e disponibilidade atendidas',
    formula: 'backup, restauração, disponibilidade e comunicação de incidentes',
    ondeNoTr: 'Anexo VIII, p. 162-166',
  },
}

/**
 * Os oito campos do plano, com o nome que o usuário entende e a pergunta que
 * cada um responde. A chave é o nome do campo na API — nunca traduza a chave.
 */
export const CAMPOS_DO_PLANO = [
  {
    campo: 'justificativa',
    rotulo: 'Por que esta mudança é necessária',
    ajuda: 'Qual problema ela resolve, ou qual necessidade atende. Escreva para quem não acompanha o dia a dia.',
  },
  {
    campo: 'preRequisitos',
    rotulo: 'O que precisa estar pronto antes',
    ajuda: 'Acessos, aprovações, backups, versões, avisos aos usuários — o que tem de existir antes de começar.',
  },
  {
    campo: 'requisitos',
    rotulo: 'O que a mudança entrega',
    ajuda: 'O resultado esperado, do ponto de vista de quem usa o sistema.',
  },
  {
    campo: 'procedimentosImplementacao',
    rotulo: 'Passo a passo da implantação',
    ajuda: 'Na ordem, quem faz e o que faz. Detalhe o suficiente para outra pessoa executar.',
  },
  {
    campo: 'planoRollback',
    termo: 'planoRollback',
    // Rótulo e ajuda vêm de TERMOS: o texto era idêntico nos dois lugares, e duas cópias da mesma
    // frase divergem na primeira vez que alguém melhorar uma delas.
    rotulo: TERMOS.planoRollback.rotulo,
    ajuda: TERMOS.planoRollback.ajuda,
  },
  {
    campo: 'planoContingencia',
    termo: 'planoContingencia',
    rotulo: TERMOS.planoContingencia.rotulo,
    ajuda: TERMOS.planoContingencia.ajuda,
  },
  {
    campo: 'testeVolta',
    rotulo: 'Como se comprova que o plano de volta funciona',
    ajuda: 'O teste que mostra que dá mesmo para reverter. Sem ele, o plano de volta é só uma intenção.',
  },
  {
    campo: 'evidenciaTesteDescricao',
    termo: 'evidencia',
    rotulo: 'Comprovação dos testes',
    ajuda: 'Relatório, log, print ou link que comprove a homologação (item 4.62 do Termo de Referência).',
  },
] as const

/**
 * Textos de interface que aparecem em mais de uma tela.
 *
 * Estado vazio, erro e carregamento são onde o sistema fala mais com o usuário e
 * onde é mais fácil um texto genérico escapar. Ficam aqui pelo mesmo motivo dos rótulos.
 */
/**
 * "N execuções sem medição" — escrita UMA vez, para as três telas que precisam dela.
 *
 * A ressalva aparece no uso da IA, no saldo em reais e no andamento do lote, e as três falam da
 * mesma coisa: o provedor não informou quantos tokens aquela chamada consumiu. Duas redações
 * paralelas envelhecem em ritmos diferentes, e a que envelhecer primeiro é a que vai dizer "0
 * unidades de uso" com cara de medição.
 *
 * @param quantas quantas execuções não trouxeram medição
 * @returns a contagem por extenso
 */
function execucoesSemMedicao(quantas: number): string {
  return quantas === 1 ? '1 execução sem medição' : `${quantas} execuções sem medição`
}

export const TEXTOS = {
  carregando: 'Carregando…',
  entrar: 'Entrar com a conta da Prefeitura',
  sair: 'Sair',
  naoInformado: 'Não informado',
  /**
   * A frase que substitui um botão que a conta não pode usar.
   *
   * Escondida a ação, fica a informação: quem só consulta continua vendo que o valor não foi
   * registrado, que ninguém conferiu o mês, que a medição falta. O que ele deixa de ver é o botão
   * que produziria um 403 — e o público declarado é leigo em TI, de modo que uma tarja vermelha
   * depois de um clique não é lida como "não é comigo", é lida como "eu quebrei o sistema".
   *
   * Uma frase só, num lugar só: ela aparece nas seis telas onde a fiscalização escreve.
   */
  somenteConsulta:
    'Quem registra isto é a fiscalização — fiscal ou administrador da Prefeitura. Sua conta abre esta ' +
    'tela para consulta, e por isso o botão não aparece.',
  /**
   * Nenhum mês tem dado, e a tela é por mês.
   *
   * O estado que a UAT vai encontrar no primeiro dia: base limpa, nenhuma competência. Sem ele, as
   * consultas ficam `enabled: false` e o esqueleto cinza pulsa para sempre — que é exatamente a
   * "tela quebrada" que o cenário 39 registra. O texto diz o que precisa acontecer para haver o que
   * mostrar, e não só que não há nada.
   */
  semCompetencia: {
    titulo: 'Nenhum mês tem chamado carregado ainda',
    ajuda:
      'Esta tela é apurada mês a mês, e um mês só existe aqui depois que os chamados dele entram no ' +
      'sistema — pela planilha da fiscalização ou pela atualização automática do e-Niterói. Carregue a ' +
      'planilha na tela de fechamento do mês; assim que houver chamado em algum mês, ele aparece aqui.',
    acao: 'Ir para o fechamento do mês',
  },
  /**
   * Entrada por usuário e senha.
   *
   * É outra coisa do `entrar` acima, que abre o login institucional: qual das duas a tela
   * oferece quem decide é `GET /auth/modos`, não o frontend. Por isso os dois textos
   * convivem aqui em vez de um substituir o outro.
   *
   * `credencialInvalida` é rede de segurança: quando a recusa vem do servidor, quem fala é
   * a mensagem dele — que já distingue senha errada de conta bloqueada por tentativas, e
   * essa diferença importa para quem está tentando entrar.
   */
  login: {
    // Sem ajuda sob o campo de usuário, e é decisão: a conta deste sistema NÃO é a mesma dos
    // outros sistemas da Prefeitura. Ela é local, criada em /admin/usuarios, e o texto que
    // afirmava o contrário mandava quem não conseguisse entrar tentar a senha de outro sistema —
    // e, no limite, trocar a senha no lugar errado.
    usuario: 'Usuário',
    senha: 'Senha',
    botao: 'Entrar',
    entrando: 'Entrando…',
    faltaPreencher: 'Preencha o usuário e a senha para entrar.',
    credencialInvalida: 'Usuário ou senha incorretos. Confira e tente de novo.',
  },
  /**
   * Os critérios com que o TR é lido — as doze contradições, resolvidas.
   *
   * O texto evita a palavra "parâmetro" no rótulo principal: quem lê é fiscal de contrato, e
   * "critério" é o que ele já usa quando discute com a contratada. A chave técnica aparece, mas
   * em segundo plano.
   */
  /**
   * O TIS aberto por incidente.
   *
   * Ele é o único indicador que não tem nota: soma um desconto por chamado corretivo que passou do
   * prazo. Os textos abaixo existem para que a lista se leia sem o contrato do lado — e para que
   * "advertência" nunca se confunda com "desconto", porque o TR trata as duas como coisas
   * diferentes (p.161) e só uma delas tira dinheiro da nota.
   */
  /**
   * O cartão da sonda própria de disponibilidade.
   *
   * Texto de oferta, e não de resultado: o número aparece para o fiscal CONFERIR e adotar se
   * quiser. Se ele lesse como apuração, a tela estaria dizendo que o sistema mediu o indicador —
   * e o indicador continua sendo o que uma pessoa registra.
   */
  sondagem: {
    titulo: 'A Prefeitura mediu esta disponibilidade por conta própria',
    fracao: (comResposta: number, total: number) =>
      `${comResposta.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} minutos com resposta`,
    medido: 'Período medido',
    diasMedidos: (dias: number) => (dias === 1 ? '1 dia' : `${dias} dias`),
    alvo: 'Endereço sondado',
    fonte: 'Instrumento',
    observado: 'Quanto do mês foi observado',
    minutosObservados: (observados: number, doMes: number) =>
      `${observados.toLocaleString('pt-BR')} dos ${doMes.toLocaleString('pt-BR')} minutos do mês`,
    ajuda:
      'Este número não substitui o da contratada: ele existe para você ter contra o que conferir. ' +
      'Se os dois divergirem, a divergência é achado de fiscalização — e quem decide qual vale é você. ' +
      'Adotar preenche os campos de disponibilidade abaixo; nada é registrado até você salvar.',
    mesIncompleto:
      'A medição não cobre o mês inteiro. Adotá-la assim registraria como "disponibilidade do mês" um ' +
      'período menor do que o mês — confira os dias e os minutos acima antes de usar. O que a sonda não ' +
      'observou não conta como fora do ar: se o sistema caiu justamente numa dessas janelas, a queda não ' +
      'aparece neste número.',
    adotar: 'Usar estes números',
    adotada: 'Números adotados',
  },
  iie: {
    advertencia: 'Advertência',
    dentroDaMeta: 'Dentro da meta',
    decidiuORedutor: (qual: string) =>
      `O desconto do mês veio de "${qual}" — é o pior dos quatro, e é ele que o contrato faz valer.`,
    semMedicao: (quais: string) =>
      `Sem medição neste mês: ${quais}. Ficam de fora da conta — não valem zero, porque zero seria a ` +
      'leitura de que tudo falhou.',
    fonte: (fonte: string) => `Números lidos de: ${fonte}.`,
    registrar: 'Registrar medição de infraestrutura',
    registrando: 'Registrando…',
    retirar: 'Retirar esta medição',
    retirarAjuda:
      'A medição continua registrada, com quem retirou, quando e por quê. Ela produziu o desconto do sexto ' +
      'indicador — por isso não se apaga nem se reescreve por cima.',
    semRegistro: {
      titulo: 'A medição de infraestrutura deste mês ainda não foi registrada',
      ajuda:
        'Os quatro números vêm das ferramentas da contratada — backup, restauração e monitoramento — e ' +
        'alguém precisa lê-los e registrá-los aqui. O sistema não os coleta sozinho, e enquanto isso o sexto ' +
        'indicador fica sem número.',
    },
    ajudaDosNumeros:
      'Informe os números BRUTOS, não os percentuais: 24 backups com sucesso em 31 executados, e não 77%. É ' +
      'a fração que se confere contra o log da ferramenta. Deixe em branco o que não houve no mês — em ' +
      'branco não é zero.',
    /**
     * A tela explica por que o número não sai quando a regra de consolidação ainda não vigora.
     *
     * O texto do impedimento vem do SERVIDOR, que é quem sabe qual parâmetro falta e desde quando.
     * Repeti-lo aqui produziria duas versões da mesma frase, e a daqui envelheceria no dia em que
     * a vigência mudasse — que é justamente a decisão que está em aberto.
     */
    semCriterio: {
      titulo: 'A medição deste mês ainda não produziria número',
      comoSeguir:
        'Guarde os números de infraestrutura deste mês com a contratada e registre-os quando a regra passar a ' +
        'valer. Registrar agora gastaria o único registro do mês sem gerar indicador — a medição não se corrige ' +
        'por cima, só se retira com motivo.',
    },
    /** O cartão da medição já registrada, com o botão que faltava. */
    registrada: {
      titulo: 'A medição de infraestrutura deste mês já está registrada',
      fonte: (fonte: string) => `Números lidos de: ${fonte}.`,
      por: (quem: string, quando: string) => `Registrada por ${quem} em ${quando}.`,
      ajuda:
        'Uma medição por mês. Para trocar um número, retire esta com o motivo e registre a correta — as duas ' +
        'ficam lado a lado, e quem conferir o processo depois vê o que mudou.',
    },
    semNenhumPar:
      'Preencha ao menos um par completo antes de registrar. Cada sub-indicador pede os DOIS números: backups ' +
      'com sucesso e executados, restaurações com sucesso e solicitadas, os três de disponibilidade, ou ' +
      'mensagens enviadas e devidas. Só a fonte preenchida gravaria uma medição que não produz número nenhum — ' +
      'e ela não se corrige por cima.',
    numerosQuebrados: (quais: string) =>
      `Estes campos não são contagens inteiras: ${quais}. Todos os nove são contagens ou minutos — escreva ` +
      'só dígitos, com ponto de milhar se ajudar ("1.200"). Sem isso a medição entraria menor do que é, e ' +
      'o sexto indicador sairia melhor do que foi.',
  },
  quadro: {
  titulo: 'Quadro das solicitações',
  subtitulo:
    'Cada coluna é uma etapa do processo de mudanças do contrato. O topo da coluna diz de quem o ' +
    'sistema está esperando.',
  irPara: 'Ir para',
  responsavelDaColuna: (quem: string) => `Esperando: ${quem}`,
  responsavelContratual: (quem: string) => `Responsável no contrato: ${quem}`,
  contagem: (quantas: number) => (quantas === 1 ? 'Uma solicitação' : `${quantas} solicitações`),
  mover: 'Mover…',
  moverAjuda:
    'Mostra o que você pode fazer com esta solicitação agora, e o que está bloqueado e por quê.',
  arrasteAjuda:
    'Arrastar um cartão propõe a mesma ação do botão — inclusive o pedido de justificativa, quando o ' +
    'contrato exige. O cartão só muda de coluna depois que o sistema confirma.',
  decisaoDoComiteSaiDoMenu:
    'As decisões do comitê — aprovar, recusar e pedir mais informações — não saem de arraste. Elas ' +
    'pedem justificativa escrita e são o que o contrato exige antes de qualquer implantação.',
  zonaNaoEhDestino: (coluna: string) =>
    `${coluna} não é um destino possível a partir do estado atual.`,
  transicaoConcluida: (codigo: string, destino: string) => `${codigo} passou para ${destino}.`,
  transicaoRecusada: (codigo: string) => `${codigo} não mudou de etapa. Veja o aviso na tela.`,
  zonas: {
    aguardandoAjustes: {
      rotulo: 'Aguardando ajustes',
      ajuda: 'O comitê devolveu pedindo correção. A próxima ação é da contratada: ajustar e reenviar.',
    },
    aprovada: {
      rotulo: 'Aprovada',
      ajuda: 'O comitê liberou. A próxima ação é da contratada: iniciar a execução na janela.',
    },
  },
  foraDoFluxo: {
    titulo: 'Fora do fluxo',
    ajuda:
      'Recusadas, canceladas e revertidas não estão em etapa nenhuma do contrato — por isso não têm ' +
      'coluna. Ficam aqui, contadas, com link para a lista.',
    ver: 'Ver na lista',
  },
  colunaVazia: 'Nada esperando nesta etapa agora.',
  vazio: {
    titulo: 'O processo novo está começando',
    ajuda:
      'As solicitações trazidas do sistema anterior não aparecem aqui: elas não percorreram este ' +
      'processo, e colocá-las numa coluna diria que percorreram. Este quadro mostra o que anda pelo ' +
      'processo do contrato novo.',
  },
  truncado: (mostradas: number, total: number) =>
    `Mostrando as ${mostradas} solicitações mais recentes de ${total}. Para ver todas, use a lista.`,
  soNoComputador: {
    titulo: 'O quadro precisa de uma tela larga',
    ajuda:
      'Sete colunas lado a lado não cabem no celular. A lista de solicitações mostra a mesma ' +
      'informação, em linhas.',
  },
  },
  etapas: {
    titulo: 'Onde esta solicitação está no processo',
    ajuda:
      'As sete etapas que o contrato define (Anexo XXI), com quem responde por cada uma. O estado diz o ' +
      'QUE está acontecendo; esta lista diz DE QUEM se está esperando.',
    responsavel: (quem: string) => `responsável: ${quem}`,
    esperandoPor: (quem: string) => `A próxima ação é de: ${quem}.`,
    foraDoFluxo:
      'Esta solicitação não está em nenhuma das sete etapas: ela foi recusada, cancelada, revertida, ou é ' +
      'registro importado do sistema anterior — que nunca percorreu este processo.',
    marcaDaDivergencia: '(ver observação abaixo)',
    divergenciaDaEtapa7:
      'Observação sobre a etapa 7: o contrato atribui o fechamento à contratada, e neste sistema quem encerra ' +
      'é a Prefeitura. A divergência está registrada e ainda não foi resolvida — ela aparece aqui em vez de ' +
      'ficar escondida, porque quem a encontrar depois será a contratada, numa contestação.',
  },
  usoDaIa: {
    titulo: 'Consumo da análise por IA',
    ajuda:
      'Quantas análises o sistema executou por mês, quantas nasceram sozinhas a partir de uma transição, ' +
      'quantas falharam e quantos tokens foram consumidos.',
    semMedicao: execucoesSemMedicao,
    emTokens:
      'Os números desta tela são unidades de uso do modelo (tokens). O consumo em reais fica em "Crédito de ' +
      'IA": lá cada chamada é convertida pelo preço que valia no instante em que ela aconteceu, e o preço ' +
      'aparece sempre ao lado do valor. "Execuções sem medição" são análises que o provedor não mediu: elas ' +
      'não valem zero, elas não foram medidas.',
    origem: {
      automatica: 'Automáticas',
      aPedido: 'A pedido',
      emLote: 'Em lote',
      ajuda:
        'Automática é a leitura que nasceu sozinha ao abrir uma tela ou a partir de uma transição. A pedido ' +
        'é o botão. Em lote é a análise de uma competência inteira, pedida pela administração.',
    },
    modeloUsado: 'Modelo',
    vazio: {
      titulo: 'Nenhuma análise executada ainda',
      ajuda:
        'As análises aparecem aqui assim que o sistema emitir o primeiro parecer — de chamado ou de ' +
        'solicitação de mudança.',
    },
  },

  /**
   * O crédito de IA do mês, o preço do token e o consumo do saldo — a tela do administrador.
   *
   * **A frase que substitui `usoDaIa.semReais`.** Aquele texto dizia que esta área não converte tokens
   * em reais; ela passou a converter. O que NÃO mudou é a razão de fundo: um R$ aqui não pode ser lido
   * como custo do contrato com a contratada. Por isso o vocabulário é "crédito", "saldo" e "consumo do
   * saldo" — as palavras do próprio dono, e as mais distantes de "custo do contrato" —, e todo valor
   * aparece com o preço e a procedência ao lado.
   */
  creditoDaIa: {
    titulo: 'Crédito de IA',
    ajuda:
      'Quanto a Prefeitura liberou, neste mês, para a análise por inteligência artificial — e quanto já foi ' +
      'consumido. É saldo junto à Microsoft Azure, o serviço que escreve os pareceres.',

    naoEhContrato:
      'Este valor é o consumo do saldo que a Prefeitura mantém junto à Microsoft Azure para a análise por ' +
      'inteligência artificial. Ele não é valor do contrato com a contratada: não entra na medição, não ' +
      'entra na glosa, não entra no termo de recebimento, não entra em nenhum indicador e não aparece em ' +
      'nenhuma tela de fiscalização. O preço do modelo muda sem aditivo nenhum — por isso cada chamada é ' +
      'convertida pelo preço do instante em que ela aconteceu, e o preço aparece sempre ao lado do valor.',

    saldo: {
      titulo: 'Saldo deste mês',
      consumidoDe: (consumido: string, credito: string) => `${consumido} de ${credito}`,
      restante: (v: string) => `Restam ${v} neste mês`,
      medidoEPresumido: (medido: string, presumido: string, quantas: number) =>
        quantas === 1
          ? `${medido} medidos · ${presumido} presumidos (1 execução sem medição de uso)`
          : `${medido} medidos · ${presumido} presumidos (${quantas} execuções sem medição de uso)`,
      tudoPresumido: (execucoes: number) =>
        `Todo o consumo deste mês é presumido: ${execucoesSemMedicao(execucoes)}, ou seja, nenhuma delas ` +
        'trouxe medição de uso do provedor. O número acima é um teto plausível, e não uma medição — não ' +
        'dimensione o crédito por ele sem saber disso.',
      emDolar: (v: string) => `${v} na moeda da fatura da Azure`,
      naReserva:
        'O saldo entrou na reserva dos últimos 20%. A leitura automática e os lotes pararam; quem precisar ' +
        'de uma leitura agora continua conseguindo pedir pelo botão, até o saldo zerar.',
      esgotado:
        'Os créditos de IA deste mês acabaram. Nenhuma leitura nova é gerada, nem pelo botão. A análise ' +
        'volta na virada do mês, ou assim que você declarar um novo crédito.',
      // A frase dizia "unidades de uso — QUE É O QUE ELE MEDE" logo antes do número, e o número ao
      // lado dela é, hoje, 100% presunção: 6 execuções × o custo presumido, nenhuma medida. Chamar
      // isso de medição é a mentira que a V31 proibiu, agora do lado das unidades. A tarja de
      // "tudo presumido" aparece logo abaixo quando for o caso; esta frase só não pode contradizê-la.
      semCredito:
        'Nenhum crédito declarado para este mês. Enquanto não houver, o sistema conta em unidades de uso, e ' +
        'os limites continuam sendo os de sempre. Declare o preço e o crédito para ver o consumo em reais. ' +
        'O sistema contabilizou',
      semPreco: (tokens: string) =>
        `${tokens} unidades de uso deste mês são anteriores ao primeiro preço declarado e não são ` +
        'convertidas. Preço não se declara para trás por cima de outro — essas ficam em unidades de uso.',
      modeloDivergente: (declarado: string, usados: string) =>
        `O preço foi declarado a partir de ${declarado}, mas este mês o sistema usou ${usados}. A conversão ` +
        'pode estar defasada — registre o preço novo se o modelo mudou. Os limites em unidades de uso ' +
        'continuam valendo do mesmo jeito.',
      oQueContinua:
        'Continuam funcionando normalmente: ler os pareceres já guardados, julgar chamados, marcar ICA, IAP ' +
        'e TIS, registrar o valor da fatura, dispensar sanções, fechar a competência, as transições de ' +
        'solicitação de mudança, a sincronização com o e-Niterói, a conferência de chamados repetidos e a ' +
        'revisão de código do Copilot. Só a geração de parecer novo para.',
    },

    credito: {
      titulo: 'Declarar o crédito do mês',
      valor: 'Quanto pode ser gasto neste mês',
      valorAjuda:
        'Ao chegar aos últimos 20% deste valor, a leitura automática e os lotes param. O botão de pedir uma ' +
        'leitura continua até zerar. Acima disso, nada mais é gerado.',
      motivo: 'Por que este valor',
      motivoAjuda:
        'Fica registrado com o seu nome e a data. É o que responde, depois, por que a análise parou num ' +
        'determinado dia. Mínimo de 10 caracteres.',
      aviso:
        'O crédito não se edita. Para trocar, revogue este e declare outro — os dois ficam lado a lado, e a ' +
        'pergunta "quanto havia no dia 18?" continua tendo resposta.',
      confirmar: 'Declarar crédito',
      revogar: 'Revogar',
      revogarMotivo: 'Por que está revogando?',
      revogado: 'Revogado',
      historico: 'Créditos declarados',
      semCreditos: 'Nenhum crédito declarado ainda.',
    },

    preco: {
      titulo: 'Preço do token',
      ajuda:
        'Quanto a Microsoft cobra por milhão de unidades de uso, e a que câmbio. A Azure cobra em dólar: ' +
        'guardamos em dólar e mostramos em real, pelo câmbio que você declarar.',
      entrada: 'Preço por milhão de unidades de entrada (o que o sistema envia)',
      saida: 'Preço por milhão de unidades de saída (o que o modelo escreve)',
      entradaSaidaAjuda:
        'Os dois têm preços diferentes, e costumam diferir de três a cinco vezes. Aqui a entrada é o chamado ' +
        'e o relato do atendimento; a saída é um parecer curto.',
      moeda: 'Moeda em que a Azure cobra',
      cambio: 'Quanto vale 1 dólar em reais',
      cambioFonte: 'De onde veio o câmbio',
      cambioEm: 'Data do câmbio',
      cambioAjuda:
        'O sistema não busca cotação, e é decisão: uma cotação que se move sozinha mudaria a conversão de ' +
        'ontem sem ninguém ter feito nada. A data aparece ao lado de todo valor.',
      cambioAntigo: (dias: number) =>
        `O câmbio declarado tem ${dias} dias. Câmbio velho não erra o que já foi convertido — cada chamada ` +
        'guarda o preço do instante dela. Ele só deixa torta a conversão das próximas.',
      fonte: 'De onde veio o preço',
      fonteAjuda:
        'Ex.: "tabela pública do Azure OpenAI, região East US 2, consultada em 27/08/2026" · "fatura Azure ' +
        'de julho/2026".',
      modelo: 'Modelo de que o preço foi tirado',
      modeloAjuda: (emUso: string) =>
        `O sistema está usando ${emUso}. O preço vale para toda unidade de uso do período, qualquer que seja ` +
        'o modelo — este campo serve para a tela avisar quando eles divergirem.',
      valeApartirDe: 'Vale a partir de',
      valeApartirDeAjuda:
        'Cada chamada é convertida pelo preço do instante em que ela aconteceu. Preço novo não mexe no que ' +
        'já foi convertido: ele vale das próximas em diante.',
      naoReprecifica:
        'Registrar um preço novo NÃO reprecifica o que já passou, e é decisão: o valor que você leu na ' +
        'semana passada não muda sozinho. Um mês em que o preço mudou aparece somando os dois preços, com ' +
        'os subtotais separados — que é o que de fato aconteceu.',
      naoEdita:
        'Preço não se edita. Feche a vigência deste e registre o novo — os dois ficam lado a lado.',
      encerrar: 'Encerrar a vigência',
      registrar: 'Registrar este preço',
      novo: 'Registrar preço',
      vigente: 'Em vigor',
      encerrado: 'Encerrado',
      semPrecos: 'Nenhum preço declarado ainda. Sem ele, esta tela mostra unidades de uso e não reais.',
      procedencia: (preco: string, cambio: string, quem: string) => `${preco} · ${cambio} · declarado por ${quem}`,
    },
  },

  /**
   * A análise em lote de uma competência.
   *
   * O vocabulário insiste em duas coisas que a tela precisa carregar em cada linha: a estimativa é uma
   * FAIXA (não há consumo medido para apontar um valor típico), e o lote não muda estado de nada — ele
   * escreve pareceres de leitura, e mais nada (Regra nº 3).
   */
  loteDeAnalise: {
    titulo: 'Analisar os chamados de um mês',
    ajuda:
      'Pede um parecer da IA para todos os chamados fechados e não julgados de uma competência. Serve para ' +
      'conferir contra o Termo de Referência os meses que foram fiscalizados fora do sistema — maio, junho ' +
      'e julho de 2026 —, que a leitura automática não alcança de propósito.',
    novo: 'Analisar uma competência',
    escolhaOMes: 'Que mês analisar',

    previa: {
      titulo: (mes: string) => `Analisar os chamados de ${mes}`,
      quantos: (n: number, mes: string) =>
        n === 1 ? `1 chamado fechado e não julgado em ${mes}.` : `${n} chamados fechados e não julgados em ${mes}.`,
      jaTem: (n: number) =>
        n === 0
          ? 'Nenhum deles já tem parecer com o texto de análise em vigor — todos serão pagos.'
          : `${n} já têm parecer com o texto de análise em vigor e não serão pagos de novo.`,
      seraoPagos: (n: number) =>
        n === 1 ? '1 chamada ao modelo será paga.' : `${n} chamadas ao modelo serão pagas.`,
      estimativaEmTokens: (min: string, max: string) =>
        `Estimativa: entre ${min} e ${max} unidades de uso.`,
      faixaSemPontoMedio:
        'A estimativa é uma faixa, e não uma promessa: ainda não há consumo medido neste sistema para ' +
        'apontar um valor típico. Ela sai da faixa declarada no projeto (3.400 a 7.300 unidades de entrada ' +
        'e 900 a 3.000 de saída por chamado).',
      estimativaEmDinheiro: (min: string, max: string) => `Em reais: entre ${min} e ${max}.`,
      estimativaSemPreco:
        'Sem preço declarado, a estimativa aparece só em unidades de uso. Declare o preço se quiser ver ' +
        'quanto isso custa.',
      saldo: (restante: string, credito: string) => `Saldo deste mês: ${restante} restantes de ${credito}.`,
      oQueNaoFaz:
        'O que este lote não faz: não julga chamado, não marca ICA, IAP nem TIS, não muda o status de nada e ' +
        'não fecha competência nenhuma. Ele só escreve pareceres de leitura, que ficam guardados para quem ' +
        'abrir cada chamado.',
      justificativa: 'Por que este mês, agora?',
      justificativaAjuda:
        'Fica registrado com o seu nome, a data e a estimativa que você está vendo agora. Mínimo de 10 ' +
        'caracteres.',
      digiteParaConfirmar: (mes: string) => `Para confirmar, digite ${mes}:`,
      confirmar: (n: number) => `Iniciar a análise de ${n} chamados`,
      jaHaUm: 'Já existe um lote em andamento. O sistema roda um de cada vez.',
      semTrabalho:
        'Nenhum chamado deste mês precisa de leitura nova: todos já têm parecer com o texto de análise em ' +
        'vigor.',
    },

    andamento: {
      titulo: (mes: string, feitos: number, total: number) => `Analisando ${mes} — ${feitos} de ${total}`,
      contagem: (novos: number, reaproveitados: number, falhas: number, adiados: number, fila: number) =>
        `${novos} com parecer novo · ${reaproveitados} reaproveitados (sem custo) · ${falhas} falharam · ` +
        `${adiados} adiados sem custo · ${fila} na fila`,
      gasto: (tokens: string) => `Gasto até agora: ${tokens} unidades de uso`,
      // A ressalva é a MESMA de `usoDaIa.semMedicao`, e por isso reaproveita a frase de lá em vez de
      // escrever uma paralela: duas redações da mesma ressalva envelhecem em ritmos diferentes.
      gastoSemMedicao: (quantas: number) =>
        `${execucoesSemMedicao(quantas)}: o provedor não informou o uso delas, e o total acima não as ` +
        'inclui. Elas não valem zero — elas não foram medidas.',
      ritmo: (minutos: number) =>
        minutos <= 1 ? 'Falta cerca de um minuto.' : `Faltam cerca de ${minutos} minutos.`,
      interromper: 'Interromper',
      interromperMotivo: 'Por que está interrompendo?',
      naoReabre:
        'Lote encerrado não reabre. Para continuar, abra outro — a estimativa é refeita contra o saldo de ' +
        'agora, e os chamados já analisados são pulados sem custo.',
      nenhum: 'Nenhum lote em andamento.',
      historico: 'Lotes pedidos',
    },

    situacaoDoLote: {
      EM_ANDAMENTO: 'Em andamento',
      PAUSADO_NO_TETO:
        'Pausado: o limite do mês foi atingido. O lote volta a rodar sozinho se a administração declarar um ' +
        'crédito maior ainda dentro deste mês. Nada foi perdido, e nada será refeito.',
      INTERROMPIDO: 'Interrompido. Os pareceres que já saíram ficam.',
      EXPIROU_NA_VIRADA:
        'Encerrado na virada do mês. O lote gasta o crédito do mês em que foi pedido; continuar em outro mês ' +
        'é um pedido novo, com estimativa nova contra o saldo novo. Os chamados já analisados são pulados ' +
        'sem custo.',
      CONCLUIDO: 'Concluído',
    },

    situacaoDoItem: {
      PENDENTE: 'Na fila',
      CONCLUIDO: 'Parecer novo',
      REAPROVEITADO: 'Reaproveitado (sem custo)',
      DISPENSADO: 'Dispensado',
      FALHOU: 'Falhou (a chamada foi paga)',
      ADIADO: 'Adiado (nada foi pago)',
      NAO_RODOU: 'Não chegou a rodar',
    },
  },
  relatorios: {
    titulo: 'Relatórios do contrato',
    ajuda:
      'Os documentos que a fiscalização produz além do termo de recebimento: o que a contratada entregou ' +
      'no mês, o que foi a produção e passou (ou não) pelo comitê, e tudo que já foi imputado a ela ao ' +
      'longo do contrato.',
    recebimento: {
      registrar: 'Registrar recebimento',
      entregue: 'Entregue',
      naoEntregue: 'Não entregue',
      atrasado: (dias: number) => (dias === 1 ? '1 dia de atraso' : `${dias} dias de atraso`),
      entregueEm: (quando: string) => `Chegou em ${quando}`,
      retirar: 'Retirar registro',
      retirarAjuda:
        'O registro continua na tabela, com quem retirou, quando e por quê. Ele atestava que a contratada ' +
        'cumpriu uma obrigação do contrato.',
      referenciaAjuda: 'Número do ofício, do processo ou do e-mail. O sistema guarda o rastro, não o arquivo.',
      tudoEntregue: 'Todos os relatórios cobrados neste mês foram registrados.',
      semPrazoFixado:
        'O contrato exige estes relatórios "mensalmente" e não escreve até que dia. Enquanto um ' +
        'administrador não fixar o prazo, esta tela responde se chegou ou não chegou — e não aponta atraso, ' +
        'porque não há prazo contra o que atrasar.',
    },
    conformidade: {
      semAprovacao: 'Sem aprovação do comitê',
      foraDaJanela: 'Executadas fora da janela',
      emergenciais: 'Declaradas emergenciais',
      aprovada: 'Passou pelo comitê',
      semAprovacaoNaTrilha: 'Sem registro de aprovação',
      aprovadaPor: (quem: string, quando: string) => `Aprovada por ${quem} em ${quando}.`,
      aprovacaoForaDaAplicacao:
        'A aprovação desta solicitação foi lançada direto no banco, sem passar pelo sistema. Ela também não ' +
        'passou pelo comitê — mas quem alterou foi a Prefeitura, não a contratada.',
      implantadaEm: (quando: string) => `Implantação declarada em ${quando}.`,
      executouForaDaJanela: 'Executou fora da janela do comitê.',
      semJustificativa: 'Sem justificativa escrita.',
      emergencial: (causa: string) => `Emergencial${causa ? ` — ${causa}` : ''}.`,
      abrir: 'Abrir a solicitação',
      semMovimento: {
        titulo: 'Nenhuma solicitação teve movimento neste mês',
        ajuda:
          'Este relatório mostra as solicitações que mudaram de etapa no mês. O histórico importado do ' +
          'sistema anterior nunca entra: ele não passou por comitê no sentido do contrato.',
      },
    },
    ocorrencias: {
      resumo: (pontos: number, quantas: number) =>
        `${pontos} ponto(s) somando em ${quantas} ocorrência(s) registrada(s) no contrato.`,
      retiradas: (quantas: number) => `${quantas} retirada(s) da soma.`,
      contouEm: (competencia: string) => `contou em ${competencia}`,
      contestacao: 'Manifestação da contratada:',
      retirada: 'Retirada da soma:',
      vazio: {
        titulo: 'Nenhuma ocorrência administrativa registrada',
        ajuda:
          'Esta lista é do contrato inteiro, e não de um mês — o contrato pune a repetição de uma mesma ' +
          'falha, e repetição não se enxerga olhando um mês por vez. As ocorrências são registradas em ' +
          'Pontos administrativos.',
      },
    },
  },
  ordensDeServico: {
  titulo: 'Ordens de serviço',
  registrar: 'Registrar ordem de serviço',
  imprimir: 'Imprimir / Salvar em PDF',
  numeroDoSistema: 'Número',
  numeroExterno: 'Número no documento',
  numeroExternoAjuda:
    'O número que a ordem de serviço já tinha quando chegou de fora deste sistema, como ' +
    '"001-SUST-MAI-2026". Os dois ficam lado a lado: nenhum é convertido no outro, porque contam ' +
    'coisas diferentes, e o sistema não corrige o que já foi assinado.',

  revogar: 'Revogar esta ordem de serviço',
  revogarAjuda:
    'Ela continua registrada, com quem revogou, quando e por quê. Uma ordem de serviço autoriza ' +
    'despesa — por isso não se apaga nem se corrige por cima. Registre a correta ao lado.',

  totalDeclarado: 'Valor no documento',
  totalDerivado: 'Conferência do sistema',
  divergenciaDoItem: (declarado: string, derivado: string) =>
    `O documento declara ${declarado}; valor unitário vezes quantidade dá ${derivado}.`,
  divergenciaAjuda:
    'O sistema mostra os dois e não corrige nenhum. O documento foi assinado como está, e quem ' +
    'decide qual número vale é quem apura — não a tela.',

  prazoNaoApuravel: {
    titulo: 'Prazo não apurável',
    semEntrega:
      'O contrato conta o prazo a partir da entrega da ordem ao preposto da contratada, e essa ' +
      'entrega não foi registrada. Não há atraso a apontar: há prazo que ainda não começou. ' +
      'Registre a entrega no andamento para o prazo passar a existir.',
    semTermoDeEncerramento:
      'O prazo do recebimento provisório conta do recebimento do termo de encerramento da ordem, e ' +
      'esse termo não foi registrado. Sem ele não há prazo a contar — e por isso o sistema não diz ' +
      'nem "em dia" nem "vencido".',
    parametroIndeterminado: (chave: string) =>
      `O critério deste prazo ainda não foi definido (${chave}). Apurar assim produziria um número ` +
      'com aparência de certo.',
  },
  prazoDaPrefeitura:
    'Este prazo é da Prefeitura, não da contratada. Ele nunca vira desconto nem ponto administrativo.',

  mesesSemOrdem: {
    titulo: (quantos: number) =>
      quantos === 1
        ? 'Um mês sem ordem de serviço registrada'
        : `${quantos} meses sem ordem de serviço registrada`,
    ajuda:
      'O contrato manda emitir uma ordem de serviço no início de cada mês, para sustentação e ' +
      'infraestrutura. Ele não diz até que dia — por isso o sistema aponta a ausência do registro e ' +
      'não afirma que está atrasada.',
    registrarDe: (mes: string) => `Registrar a de ${mes}`,
    nenhum: 'Todos os meses cobertos por este sistema têm ordem de serviço registrada.',
    /**
     * Por que a conta não começa no começo do contrato.
     *
     * O contrato vale desde 06/05/2026, mas a fiscalização passou a trabalhar aqui dentro só em
     * agosto: maio, junho e julho foram medidos à mão, e as ordens daqueles meses existem em
     * papel. Sem esta frase, quem conhece o contrato abriria a tela e concluiria que o sistema não
     * está vendo três meses — e a explicação certa é o contrário.
     */
    antesDoSistema:
      'Os meses anteriores à entrada deste sistema não entram nesta conta: a fiscalização deles foi ' +
      'feita fora daqui, e as ordens daquele período existem em papel.',
  },

  divisao: {
    titulo: 'A divisão por órgão não está aqui',
    ajuda:
      'O modelo do contrato tem uma seção que reparte o valor entre secretarias. O contrato a ' +
      'menciona uma única vez, não explica o que ela decide, e nenhum documento do contrato a ' +
      'preencheu até hoje. Enquanto não houver decisão formal, o sistema não a inventa.',
  },

  naoSeEdita: {
    titulo: 'Ordem de serviço não se edita',
    ajuda:
      'Ela autoriza despesa e é o marco de que o contrato conta prazo. Se algo está errado, ' +
      'revogue-a — fica quem revogou, quando e por quê — e registre a correta. As duas ficam lado a lado.',
  },

  vazio: {
    titulo: 'Nenhuma ordem de serviço registrada',
    ajuda:
      'O contrato manda emitir uma ordem de serviço no início de cada mês, e registrá-las aqui é o ' +
      'que permite conferir depois se o que foi cobrado tinha autorização.',
    ondeEstaOHistorico:
      'As ordens do contrato anterior não foram trazidas para cá: elas usam uma unidade de medida ' +
      'que este contrato extinguiu, e nenhuma delas chegou a ser encerrada. Elas continuam ' +
      'disponíveis no sistema anterior.',
  },

  osEProcedencia:
    'A ordem de serviço diz de onde o valor faturado veio. Ela não muda o cálculo do desconto: o ' +
    'desconto continua incidindo sobre um valor único do mês, com um teto de 10%.',

  // ————— O que a lista, o detalhe e o documento repetem entre si —————

  semResultados: {
    titulo: 'Nenhuma ordem de serviço com esses filtros',
    ajuda: 'Nenhuma ordem atende ao recorte escolhido. Limpe os filtros para ver todas.',
  },
  limparFiltros: 'Limpar filtros',
  voltarParaALista: 'Voltar para as ordens de serviço',
  verDocumento: 'Ver o documento',
  competencia: 'Mês',
  periodo: 'Período',
  emitidaEm: 'Emitida em',
  situacao: 'Situação',
  valor: 'Valor',
  prazos: 'Prazos de recebimento',
  vence: 'Vence em',
  registradaPor: (quem: string, quando: string) => `Registrada por ${quem} em ${quando}`,
  revogadaPor: (quem: string, quando: string) => `Revogada por ${quem} em ${quando}`,

  abas: {
    identificacao: 'Identificação',
    itens: 'Itens e valores',
    andamento: 'Andamento',
  },

  andamento: {
    titulo: 'O que já aconteceu nesta ordem',
    ajuda:
      'Cada linha é um ato datado, com o documento que prova a data e quem o registrou aqui. É desta ' +
      'lista que os prazos do contrato saem — sem ato registrado, não há prazo a contar.',
    vazio: 'Nada registrado ainda. A ordem foi emitida e ninguém informou o que aconteceu depois.',
    registrar: 'Registrar o que aconteceu',
    revogarFato: 'Retirar este registro',
    revogarFatoAjuda:
      'Ele continua na tabela, com quem retirou e por quê: deste registro corre prazo contratual, e ' +
      'mudar a data por cima apagaria a prova de qual prazo valia.',
    meio: 'Meio',
    referencia: 'Onde está',
    responsavelDoAto: (quem: string) => `Ato da ${quem.toLowerCase()}`,
  },

  regimes: {
    SUSTENTACAO_INFRAESTRUTURA: 'Mensal (sustentação e infraestrutura)',
    PROJETO_PONTO_DE_FUNCAO: 'Projeto (Pontos de Função)',
  },
  tipos: {
    INICIAL: 'Inicial',
    FINAL: 'Final',
  },
  meios: {
    OFICIO: 'Ofício',
    EMAIL_INSTITUCIONAL: 'E-mail institucional',
    ENITEROI: 'e-Niterói',
    REUNIAO_GRAVADA: 'Reunião gravada',
    ATA: 'Ata',
    OUTRO: 'Outro meio formal',
  },
  lados: {
    CONTRATADA: 'contratada',
    CONTRATANTE: 'Prefeitura',
  },
  },
  valorDaFatura: {
    titulo: 'Valor faturado e desconto em reais',
    valorFaturado: 'Valor faturado',
    glosaApurada: 'Desconto apurado',
    glosaAplicada: 'Desconto aplicado',
    valorFinal: 'Valor final a pagar',
    registrar: 'Registrar valor faturado',
    registradoPor: (quem: string) => `Registrado por ${quem}`,
    retirar: 'Retirar este valor',
    retirarAjuda:
      'O valor continua registrado, com quem retirou, quando e por quê. Ele multiplicou o percentual e ' +
      'pode ter produzido um desconto — por isso não se apaga nem se reescreve por cima.',
    fundamentoAjuda:
      'Número da nota fiscal, da ordem de serviço, ou o cálculo proporcional de dias. Valor digitado sem ' +
      'procedência não se sustenta quando a contratada contesta.',
    ordemDeServico: 'Ordem de serviço a que este valor se refere',
    ordemDeServicoAjuda:
      'A ordem de serviço diz de onde o valor faturado veio. Ela não muda o cálculo do desconto: o ' +
      'desconto continua incidindo sobre um valor único do mês, com um teto de 10%.',
    semValor: {
      titulo: 'O valor faturado deste mês ainda não foi registrado',
      ajuda:
        'Sem ele o sistema mostra o percentual de desconto e não afirma quantia nenhuma — e o termo de ' +
        'recebimento sai sem reais. Não precisa esperar o fechamento: a nota fiscal costuma chegar depois, e ' +
        'o valor pode ser registrado a qualquer momento.',
    },
  },
  trp: {
    voltar: 'Voltar para o fechamento',
    imprimir: 'Imprimir / Salvar em PDF',
    ajudaDaBarra:
      'Na janela de impressão, escolha "Salvar como PDF" no lugar da impressora para gerar o arquivo. ' +
      'Confira o documento na tela antes: ele sai exatamente assim.',
    emitir: 'Emitir termo de recebimento',
    soFechada: {
      titulo: 'O termo só sai de competência fechada',
      ajuda: (competencia: string) =>
        `${competencia} ainda está em preparação: o número muda a cada julgamento seu. Um termo emitido ` +
        'agora poderia mostrar um valor na impressão e outro na hora da assinatura. Feche a competência e ' +
        'volte aqui — o documento sai do número congelado.',
    },
  },
  indicadores: {
    titulo: 'Indicadores de medição (IMR)',
    ajuda:
      'Os seis indicadores do Anexo VIII, mês a mês. Clique numa competência para abrir o fechamento ' +
      'dela, com o caminho de cada número até os chamados que o formaram.',
    legenda: 'Os seis indicadores do Anexo VIII por competência, com o desconto de cada mês.',
    previa: 'Prévia — ainda muda',
    congelada: (versao: number) => (versao > 1 ? `Fechada (${versao}º fechamento)` : 'Fechada'),
    congeladoIlegivel:
      'O número deste mês foi congelado num formato que o sistema não lê mais. Reabra e feche de novo.',
    somaDeDescontos: 'soma',
    tetoCortou: (soma: number) => `(${soma}% somados)`,
    dispensada: 'Sanções dispensadas no período de ajuste',
    versoesDiferentes: (versoes: string) =>
      `Esta série mistura ${versoes} versões diferentes do cálculo. Meses apurados por fórmulas ` +
      'diferentes não se comparam diretamente: uma linha que sobe pode ser a regra que mudou, e não o ' +
      'desempenho.',
    semNumeroNaoEhZero:
      'Célula com traço significa que o indicador não pôde ser apurado naquele mês, e o motivo aparece ao ' +
      'passar o mouse. Não é zero: zero seria o resultado de quem cumpriu a meta.',
    semCompetencias: {
      titulo: 'Nenhuma competência apurada ainda',
      ajuda:
        'A série aparece assim que houver um mês com chamados carregados ou com o fechamento iniciado.',
      acao: 'Ir para o fechamento do mês',
    },
    evolucao: {
      titulo: 'Desconto na nota, mês a mês',
      ajuda:
        'A altura da barra é medida contra o teto do contrato, e não contra o maior mês da série — é o que ' +
        'permite ver se a execução está se aproximando do limite.',
      escala: (teto: number) => `Escala fixa: o topo da barra é o teto de ${teto}% do contrato.`,
      leitura: (competencia: string, redutor: number) => `${competencia}: ${redutor}% de desconto na nota.`,
    },
  },
  iad: {
    ajuda:
      'Os pontos administrativos do mês, somados, decidem um desconto na nota fiscal. Cada ocorrência vale a ' +
      'pontuação que o contrato fixa — quem registra não escolhe o número.',
    semOcorrencias: {
      titulo: 'Nenhuma ocorrência registrada neste mês',
      ajuda:
        'Registre aqui os descumprimentos administrativos que você apurou: prazo de indicação de preposto, ' +
        'currículo não enviado, acesso não concedido, implantação sem passar pelo comitê. Cada um tem ' +
        'pontuação fixa no contrato.',
    },
    semCandidatas: {
      titulo: 'Nenhuma solicitação para conferir',
      ajuda:
        'Toda solicitação que chegou à execução tem registro da aprovação do comitê. É o resultado esperado — ' +
        'esta lista existe para o caso em que ele falta.',
    },
    pontosDoMes: (pontos: number) => (pontos === 1 ? '1 ponto no mês' : `${pontos} pontos no mês`),
    semDesconto: 'Sem desconto na nota',
    desconto: (percentual: number) => `−${percentual}% na nota fiscal`,
    faltamParaAProximaFaixa: (quantos: number, percentual: number) =>
      quantos === 1
        ? `Falta 1 ponto para o desconto subir para ${percentual}%.`
        : `Faltam ${quantos} pontos para o desconto subir para ${percentual}%.`,
    naUltimaFaixa: 'Esta já é a última faixa do contrato: mais pontos não aumentam o desconto.',
    naoConferido: {
      titulo: 'Ninguém conferiu este mês ainda',
      ajuda:
        'Enquanto isso, este indicador fica sem número no fechamento. Não é um erro: zero ponto significa ' +
        '"nada aconteceu", e o sistema não tem como saber sozinho se nada aconteceu ou se ninguém olhou.',
      botao: 'Conferi as ocorrências deste mês',
    },
    conferidoPor: (quem: string, quando: string) => `Conferido por ${quem} em ${quando}.`,
    registrar: 'Registrar ocorrência',
    registrando: 'Registrando…',
    revogar: 'Retirar da soma',
    revogada: (quem: string) => `Retirada da soma por ${quem}`,
    contestacao: 'Manifestação da contratada',
    registrarContestacao: 'Registrar manifestação',
    contestacaoAjuda:
      'O que a contratada respondeu, por ofício ou e-mail, transcrito por você. Ela não tem acesso a esta ' +
      'tela — só ao módulo de solicitações de mudança. Registrar a manifestação não retira os pontos: para ' +
      'isso, use "Retirar da soma".',
    confirmar: 'Confirmar: houve infração',
    descartar: 'Descartar: não houve',
    confirmarAjuda:
      'Isto lança 10 pontos contra a contratada. Escreva o que você apurou — é o texto que ela vai ler se ' +
      'contestar.',
    descartarAjuda:
      'Escreva por que esta implantação não é infração. Sem o motivo, quem revisar depois não sabe se ela foi ' +
      'analisada ou apenas tirada da frente. A solicitação não volta para esta lista.',
    foraDaAplicacao:
      'A aprovação desta solicitação foi lançada direto no banco, sem passar pelo sistema. Ela também não ' +
      'passou pelo comitê — mas quem alterou foi a Prefeitura, não a contratada. Confira antes de imputar.',
    entrouEmExecucao: (quando: string) => `Entrou em execução em ${quando}`,
    implantadaEm: (quando: string) => `Implantação declarada em ${quando}`,
  },
  tis: {
    tetoAlcancado: (soma: number, teto: number) =>
      `Os atrasos deste mês somam ${soma}% de desconto, e o contrato limita este indicador a ${teto}%. ` +
      `O que entra na nota é ${teto}% — o número acima é o limite, não o total apurado.`,
    horasUteis: (horas: string) => `${horas} horas úteis até a solução`,
    // A conta aparece inteira: bruto − espera = o que vale. Sem os três números, o desconto é um
    // número menor que o fiscal teria de acreditar; com eles, é uma conta que ele confere.
    descontoDePendente: (brutas: string, espera: string) =>
      `${brutas} h brutas − ${espera} h em "Pendente"`,
    semDescontoNesteChamado: 'sem desconto de "Pendente": esta fonte não informa a espera',
    atraso: (horas: string) => `${horas} além do prazo`,
    advertencia: 'Advertência',
    noPrazo: 'No prazo',
    divergencia: (daCategoria: string) =>
      `o e-Niterói classificava como ${daCategoria.toLowerCase()}, e o fiscal reclassificou`,
    advertencias: (quantas: number) =>
      quantas === 1
        ? 'Uma advertência neste mês. Advertência não desconta da nota, mas fica registrada.'
        : `${quantas} advertências neste mês. Advertência não desconta da nota, mas fica registrada.`,
    semCriticidade: (quantos: number) =>
      `${quantos === 1 ? 'Um chamado corretivo ficou' : `${quantos} chamados corretivos ficaram`} fora da conta ` +
      'por não ter criticidade nem no e-Niterói nem na marcação do fiscal. Sem criticidade não há prazo ' +
      'contra o que medir, e o sistema não escolhe uma por você:',
    semMedicao: (quantos: number) =>
      `${quantos === 1 ? 'Um chamado corretivo não pôde' : `${quantos} chamados corretivos não puderam`} ser ` +
      'medido por falta de data de solução:',
    semDescontoDeSuspensoes:
      'Nem todos os chamados acima tiveram o tempo em "Pendente" descontado — os marcados são os que a ' +
      'fonte não informou, e nesses o tempo medido pode estar inflado. O desconto é o que a nota 4a do ' +
      'TR manda fazer (p.161-162). Onde ele não pôde ser feito, quem decide se o atraso é da contratada ' +
      'é você.',
  },
  criterios: {
    titulo: 'Com que regra este mês foi apurado',
    vigentes:
      'Os critérios em vigor nesta competência. O Termo de Referência se contradiz em doze pontos, e ' +
      'cada um deles decide dinheiro — aqui está como o sistema resolve cada um, e de onde no contrato ' +
      'isso sai.',
    carimbados:
      'Os critérios que valeram QUANDO este mês foi fechado, e não os de hoje. Se algum mudou depois, o ' +
      'número acima continua sendo o que foi apurado com a regra da época.',
    abrir: (quantos: number) => `Ver os ${quantos} critérios`,
    recolher: 'Recolher',
    resumo: (provisorios: number, indeterminados: number) => {
      const partes: string[] = []
      if (provisorios > 0) {
        partes.push(
          `${provisorios} ${provisorios === 1 ? 'critério ainda é provisório' : 'critérios ainda são provisórios'} — ` +
            'o sistema opera assim até a Prefeitura formalizar por ato publicado',
        )
      }
      if (indeterminados > 0) {
        partes.push(
          `${indeterminados} ${indeterminados === 1 ? 'está indeterminado' : 'estão indeterminados'}: o contrato não ` +
            'resolveu e ninguém integrou, então o indicador que depende deles aparece como "não apurado" em vez de ' +
            'receber um número',
        )
      }
      return partes.length === 0 ? 'Todos os critérios estão formalizados por ato publicado.' : `${partes.join('; ')}.`
    },
    fonte: 'No contrato',
    risco: 'O que se perde ao decidir assim',
    ato: 'Ato que formalizou',
    status: {
      PROVISORIO: 'Provisório',
      FORMALIZADO: 'Formalizado',
      INDETERMINADO: 'Indeterminado',
    },
  },
  /**
   * As comprovações de uma solicitação.
   *
   * O texto insiste na diferença entre EXISTIR e PRESTAR: o sistema exige que haja comprovação, e
   * quem julga se ela serve é o fiscal e o comitê. Uma tela que dissesse "comprovação validada"
   * afirmaria uma conferência que o software não faz.
   */
  anexos: {
    ajuda:
      'Os arquivos que comprovam o que foi feito: relatório de teste, print da homologação, log da ' +
      'execução. Para concluir a solicitação é preciso pelo menos uma comprovação de teste — sem ela, ' +
      'quem assina o encerramento não tem o que apresentar depois. O sistema confere se o arquivo está ' +
      'aqui; se ele serve, quem decide é você.',
    categoriaAjuda:
      'Escolha antes de anexar. Só a "comprovação de teste" libera a conclusão da solicitação; as outras ' +
      'ficam registradas como apoio.',
    escolher: 'Escolher arquivo',
    enviando: 'Enviando…',
    formatos: 'Até 25 MB. PDF, imagem, planilha, documento, texto ou ZIP.',
    baixar: 'Baixar',
    excluir: 'Excluir',
    confirmarExclusao: {
      titulo: 'Excluir esta comprovação',
      confirmar: 'Excluir comprovação',
      ajuda: (nome: string) =>
        `"${nome}" sai da lista desta solicitação, e o arquivo sai do armazenamento se nenhuma outra ` +
        'solicitação apontar para o mesmo conteúdo. Não há como desfazer: a comprovação teria de ser ' +
        'anexada de novo, por quem a tem. Se ela é a comprovação de teste que permitiu concluir a ' +
        'solicitação, o sistema recusa.',
    },
    impressao: 'Impressão do arquivo',
    naoAnexou: 'Não foi possível anexar',
    naoCarregou: 'Não foi possível carregar as comprovações',
    naoExcluiu: 'Não foi possível excluir',
    vazio: {
      titulo: 'Nenhuma comprovação anexada',
      ajuda:
        'Anexe aqui o que comprova o que foi feito. Enquanto não houver ao menos uma comprovação de ' +
        'teste, o botão de concluir a solicitação continua recusando.',
    },
  },
  /**
   * As listas de apoio e o calendário do contrato.
   *
   * O texto insiste em duas coisas que a tela não pode deixar implícitas: que desativar não é
   * apagar (o histórico continua citando o item), e que mexer no calendário muda prazo já medido.
   */
  referencias: {
    titulo: 'Parâmetros e listas de apoio',
    ajuda:
      'As opções que aparecem nos campos de escolha do sistema, o calendário que decide quando o ' +
      'relógio do prazo anda, o prazo de entrega dos relatórios da contratada e os critérios com que o ' +
      'sistema resolve as contradições do contrato. Mudar aqui vale na hora, sem precisar de nova ' +
      'versão do sistema.',
    codigoAjuda:
      'Identificador que não muda nunca — letras sem acento, números e sublinhado. É por ele que outros ' +
      'sistemas e os filtros salvos encontram o item.',
    rotuloAjuda: 'O texto que as pessoas leem. Este, sim, pode ser corrigido depois.',
    naoCarregou: 'Não foi possível carregar a lista',
    naoCadastrou: 'Não foi possível cadastrar',
    naoSalvou: 'Não foi possível salvar',
    calendario: {
      titulo: 'Calendário do contrato (dias não úteis)',
      ajuda:
        'Os dias em que o relógio do prazo não anda: feriados e pontos facultativos. É este calendário ' +
        'que decide se um chamado foi atendido dentro do prazo.',
      aviso:
        'Cuidado: acrescentar ou remover um dia muda o prazo de TODO chamado que atravesse aquela data, ' +
        'inclusive os já fechados — o prazo é recalculado na próxima vez que a tela abrir. É isso que se ' +
        'quer quando um feriado estava faltando; não é o que se quer por engano.',
      naoConferido: 'ainda não conferido com o e-Niterói',
      conferido: 'conferido',
      conferir: 'Marcar como conferido',
      conferindo: 'Marcando…',
      remover: 'Remover do calendário',
      /**
       * O aviso que explica por que a conferência não é burocracia.
       *
       * Enquanto houver um dia por conferir, o TIS não apura em NENHUMA competência — nem nas
       * futuras. A tela mostrava "ainda não conferido" ao lado de cada dia e não dizia o que isso
       * custava, nem oferecia o botão de conferir. Ficaram 32 dias parados assim.
       */
      pendencia: (quantos: number, total: number) =>
        `${quantos} de ${total} dias ainda não foram conferidos com o calendário do e-Niterói. ` +
        'Enquanto houver algum, o indicador de suporte técnico (TIS) não é apurado em nenhum mês — ' +
        'nem nos futuros, porque a contagem de horas úteis depende deste calendário.',
      tudoConferido: (total: number) =>
        `Os ${total} dias do calendário estão conferidos com o e-Niterói. A contagem de horas úteis ` +
        'do TIS pode ser apurada.',
      comoConferir:
        'Conferir é abrir o calendário do e-Niterói e verificar se a ferramenta que carimba o prazo no ' +
        'chamado também trata esse dia como não útil. Quando as duas discordam, o prazo que o contrato ' +
        'cobra e o prazo que o chamado mostra divergem — e é a contratada que aponta isso primeiro.',
    },
    /**
     * O prazo dos relatórios da contratada.
     *
     * O bloco existe para tornar possível um ato que só existia por `curl`: fixar o dia limite de
     * entrega. Sem ele, "não entregue" NUNCA virava "atrasado", em mês nenhum — não porque alguém
     * tenha decidido não cobrar, mas porque ninguém conseguia fixar o dia.
     *
     * Os textos são o que separa uma configuração de um ATO: fixar o dia cria obrigação que o
     * contrato não criou, e quem passa a respondê-la é a contratada. Quem clica precisa ter lido
     * isso antes de clicar.
     */
    prazos: {
      titulo: 'Prazos dos relatórios',
      ajuda:
        'Os relatórios que o contrato obriga a contratada a entregar todo mês, e até que dia do mês ' +
        'seguinte a entrega conta como feita no prazo.',
      oQueOContratoDiz:
        'O contrato exige os relatórios "mensalmente" e NÃO escreve o dia (item 4.1.2.4.1, p.16; ANEXO ' +
        'XXV, itens 5 e 8). Enquanto não houver dia fixado aqui, a tela de relatórios responde apenas ' +
        '"entregue" ou "não entregue" — ela nunca aponta atraso, porque não há prazo contra o que atrasar.',
      oQueFixarCria:
        'Fixar um dia CRIA uma obrigação que o contrato não criou, e quem passa a respondê-la é a ' +
        'contratada: a partir daí a entrega fora do dia aparece como atraso, contado em dias, nos ' +
        'documentos da fiscalização. É ato da CONTRATANTE — não é ajuste de sistema.',
      efeitoRetroativo:
        'O atraso é calculado na hora em que a tela abre, e não gravado no registro. Um dia fixado hoje ' +
        'passa a apontar atraso também nas entregas de meses já registrados; tirar o dia devolve todas ' +
        'elas a "entregue", sem atraso.',
      semPrazo: 'Sem dia fixado',
      comPrazo: (dia: number) => `Em dia até o dia ${dia} do mês seguinte`,
      dia: 'Dia do mês seguinte',
      diaAjuda: 'De 1 a 31. Num mês que não tenha o dia escolhido, o prazo termina no último dia dele.',
      fixar: 'Fixar este prazo',
      tirar: 'Voltar a não ter prazo',
      naoSalvou: 'Não foi possível mudar o prazo',
    },
    /**
     * Os critérios do TR.
     *
     * Leitura, e uma escrita que é sempre versão nova. O texto tem de dizer três coisas que a
     * pessoa não deduz da tela: que não existe editar (o banco recusa), o que cada selo significa,
     * e que uma vigência FUTURA é a causa de um indicador ficar sem número nos meses anteriores a
     * ela — que é exatamente o caso do critério do IIE.
     */
    criterios: {
      titulo: 'Critérios do Termo de Referência',
      ajuda:
        'Como o sistema resolve cada ponto em que o contrato se contradiz. Cada critério traz de onde ' +
        'ele sai no contrato, por que se resolve assim, e o que se perde ao resolver assim.',
      semEdicao:
        'Não existe editar, e não é limitação: um critério que já sustentou um desconto assinado não se ' +
        'reescreve por cima — o banco recusa a alteração e a exclusão. Alterar é registrar uma VERSÃO ' +
        'NOVA. A anterior fica na lista, com a competência em que deixou de valer, porque é ela que ' +
        'explica o que já foi apurado.',
      selo: {
        FORMALIZADO: 'Formalizado',
        PROVISORIO: 'Provisório',
        INDETERMINADO: 'Sem critério definido',
      } as Record<string, string>,
      seloAjuda: {
        FORMALIZADO: 'Há ato publicado da Prefeitura sustentando esta leitura do contrato.',
        PROVISORIO:
          'O sistema opera assim até a Prefeitura formalizar. É o selo que diz à contratada que o ' +
          'critério ainda é discutível.',
        INDETERMINADO:
          'O contrato não resolveu e ninguém integrou ainda. O indicador que depende deste critério NÃO ' +
          'é apurado: ele fica sem número, em vez de receber um valor com aparência de certo.',
      } as Record<string, string>,
      valeDesde: (mes: string) => `Vale desde ${mes}`,
      valeAte: (mes: string) => `, e deixou de valer em ${mes}`,
      aindaNaoVale: (mes: string) =>
        `Ainda não vale: passa a valer em ${mes}. Nos meses anteriores a essa competência, o que depende ` +
        'deste critério fica SEM NÚMERO — não é falha da tela, é a ausência do critério.',
      registradoPor: (nome: string, quando: string) => `Registrado por ${nome} em ${quando}`,
      semRegistro: 'Veio com a instalação do sistema, junto da leitura do contrato.',
      risco: 'O que se perde ao resolver assim:',
      ato: 'Ato que formalizou:',
      registrar: 'Registrar versão nova',
      naoCarregou: 'Não foi possível carregar os critérios',
      novaVersao: {
        titulo: 'Registrar versão nova de um critério',
        ajuda:
          'A versão nova passa a valer a partir da competência que você escolher. A anterior não é ' +
          'apagada: ela fica com a vigência encerrada, explicando o que foi apurado até lá.',
        criterio: 'Qual critério',
        valor: 'Conteúdo do critério',
        valorAjuda:
          'No mesmo formato do que está em vigor (JSON) — copie o valor atual, que aparece na lista, e ' +
          'altere só o que muda.',
        vigencia: 'Passa a valer na competência',
        vigenciaAjuda:
          'No formato AAAA-MM. Se já houver critério valendo naquele mês, o registro é recusado: versão ' +
          'nova não reabre mês já medido. Quando NÃO houver — é o caso de um critério que só começa a ' +
          'valer no futuro —, a versão nova ocupa os meses anteriores, e o indicador passa a ter número ' +
          'neles.',
        selo: 'Selo',
        fonte: 'Onde isso está no contrato',
        fonteAjuda: 'Item e página. É o que a contratada usa para conferir o critério; vai impresso ao lado do número.',
        fundamento: 'Por que se resolve assim',
        risco: 'O que se perde ao resolver assim (opcional)',
        ato: 'Ato da Prefeitura que formalizou',
        atoAjuda:
          'Tipo, autoridade, data e publicação. Obrigatório quando o selo é "formalizado": sem o ato, o ' +
          'selo na tela estaria afirmando algo que não aconteceu.',
        registrar: 'Registrar versão',
        naoRegistrou: 'Não foi possível registrar a versão',
      },
    },
  },
  /**
   * "Esperando você": a caixa de entrada por papel.
   *
   * O bloco anterior dizia "esperando alguém" e era honesto: a lista era por estado, não por
   * pessoa. O presidente do comitê via as solicitações em execução da contratada, e o executor
   * via as que esperam decisão do comitê.
   */
  pendencias: {
    proximaAnalise: (quando: string) => `Próxima análise do comitê: ${quando}.`,
    proximaAnaliseAjuda: 'Os dias de análise e a janela de implantação estão no calendário do comitê.',
    semProximaAnalise: 'Não há dia de análise do comitê nos próximos 90 dias — confira o calendário.',
    titulo: 'Esperando você',
    ajuda:
      'O que depende de você agora, pelo seu papel no contrato. Some da tela quando não há nada — se ' +
      'este bloco não aparece, é porque não há nada esperando por você.',
    naoCarregou: 'Não foi possível carregar suas pendências',
  },
  /**
   * A pauta do comitê.
   *
   * O texto insiste numa distinção que a tela tem de carregar em cada linha: recomendação de IA e
   * revisão do Copilot são MATERIAL, não decisão. A tela mostra os dois em destaque justamente
   * porque eles ajudam — e é por isso que o risco de alguém lê-los como veredito existe.
   */
  cab: {
    titulo: 'Comitê de Mudanças (CAB)',
    ajuda:
      'O que está esperando decisão do comitê, na ordem em que vale discutir: primeiro o que já está em ' +
      'avaliação, depois o que aguarda pauta, por fim o que voltou para quem pediu. Quem espera há mais ' +
      'tempo aparece antes.',
    naoDecideAqui:
      'Esta tela não aprova nem recusa nada. Ela reúne o material; a decisão é registrada dentro de cada ' +
      'solicitação, pelo presidente do comitê, com a fundamentação obrigatória.',
    filaVazia: {
      titulo: 'Nada esperando o comitê',
      ajuda:
        'Nenhuma solicitação está aguardando pauta, em avaliação ou aguardando ajustes. Quando alguém enviar ' +
        'uma solicitação ao comitê, ela aparece aqui.',
    },
    apoio: 'Material de apoio',
    semParecer: 'Sem parecer',
    semParecerAjuda:
      'Ninguém pediu a análise automática desta solicitação ainda. Abra a solicitação para pedir — ou decida ' +
      'sem ela, que é o que se fez até agora.',
    iaDesligada:
      'A análise automática está desligada neste ambiente, então nenhuma solicitação da fila tem parecer. É ' +
      'diferente de "o modelo não achou nada": ninguém perguntou.',
    prs: (quantos: number, revisados: number) =>
      quantos === 0
        ? 'Nenhum PR citando esta solicitação'
        : `${quantos} PR${quantos > 1 ? 's' : ''} vinculado${quantos > 1 ? 's' : ''}, ${revisados} revisado${revisados === 1 ? '' : 's'}`,
    pedeMudanca: 'O Copilot pediu mudanças em um dos PRs',
    esperaHa: (dias: number) => (dias === 0 ? 'Chegou hoje' : dias === 1 ? 'Espera há 1 dia' : `Espera há ${dias} dias`),
    abrir: 'Abrir e decidir',
    abrirSemDecidir: 'Abrir',
    grupo: (rotulo: string, quantas: number) => `${rotulo} (${quantas})`,
    grupoDevolvidas: 'esperando a contratada reenviar',
    calendario: {
      titulo: 'Calendário do comitê e janelas',
      ajuda:
        'Quando o comitê analisa e a partir de que hora o que foi aprovado pode entrar em produção. Os dias são ' +
        'configuração do sistema, e não texto fixo: eles já mudaram uma vez.',
      diasDeAnalise: 'O comitê analisa nestes dias',
      janelaDeImplantacao: 'A implantação abre a partir de',
      proxima: 'Próxima abertura',
      abertaAgora: 'A janela está aberta agora',
      fechadaAgora: 'A janela está fechada neste momento',
      foraDaJanela:
        'Implantar fora da janela não é bloqueado pelo sistema — é fato contratual, que a fiscalização ' +
        'registra e que pode virar ponto administrativo. Quando acontecer, escreva a justificativa na ' +
        'solicitação.',
      /**
       * O que entra na próxima janela (pedido do Teste de uso 1).
       *
       * O sistema não agenda solicitação em janela, e a tela tem de dizer isso: sem a frase, a lista
       * pareceria uma agenda marcada — e a solicitação que aguarda pauta pareceria esquecida.
       */
      naJanela: {
        titulo: 'Entram nesta janela',
        aprovadas: 'Aprovadas — podem ir a produção a partir da abertura',
        emAvaliacao: 'Com o comitê — a decisão sai neste dia',
        nenhuma: 'Nenhuma solicitação aprovada ou com o comitê neste momento.',
        comoEntra:
          'Entra na próxima janela o que o comitê já aprovou e o que ele está avaliando. O que ainda aguarda ' +
          'pauta não aparece aqui.',
        naoCarregou: 'Não foi possível carregar as solicitações desta janela',
      },
    },
  },
  /**
   * "Minha conta": o que a pessoa é no contrato, dito para ela própria.
   *
   * O lado do contrato aparece aqui em primeiro lugar porque é a resposta da pergunta que a
   * segregação faz surgir. Quem tenta abrir a fiscalização e recebe a recusa vai atrás do motivo,
   * e o motivo tem de estar num lugar previsível — não numa mensagem de erro que já sumiu.
   */
  perfil: {
    titulo: 'Minha conta',
    ajuda: 'Seus dados, o seu lado no contrato, o que cada papel seu permite, e a troca da sua senha.',
    dados: 'Seus dados',
    ladoDoContrato: 'Lado do contrato',
    CONTRATANTE:
      'Prefeitura (contratante). Você acompanha e fiscaliza a execução do contrato: chamados, indicadores e ' +
      'fechamento do mês.',
    CONTRATADA:
      'Empresa contratada. Você trabalha nas solicitações de mudança. As telas de fiscalização são da equipe ' +
      'da Prefeitura, que mede a execução e assina o termo de recebimento.',
    semLado:
      'Ainda não há organização cadastrada na sua conta. Sem ela o sistema não sabe de que lado do contrato ' +
      'você está, e as telas de fiscalização ficam fechadas. Peça ao administrador para completar o cadastro.',
    meusPapeis: 'O que você pode fazer',
    semPapel: 'Sua conta ainda não tem papel nenhum além de consulta. Peça ao administrador o papel que falta.',
    trocarSenha: 'Trocar a minha senha',
    trocarSenhaAjuda:
      'A troca vale só para este sistema. A sua senha do sistema anterior é outra, e trocar uma não troca a outra.',
    senhaTrocada: 'Pronto. Sua senha foi trocada.',
    /**
     * A correção do próprio nome e e-mail (pedido do Teste de uso 1).
     *
     * A organização fica de fora e a tela diz por quê: ela decide o lado do contrato, e é isso que
     * separa quem executa de quem fiscaliza.
     */
    corrigir: {
      abrir: 'Corrigir nome ou e-mail',
      nome: 'Nome',
      nomeAjuda: 'Aparece ao lado de cada ato que você registra no sistema.',
      email: 'E-mail',
      emailAjuda: 'É com ele que você entra no sistema.',
      senhaAtual: 'Senha atual',
      senhaAtualAjuda: 'Pedida porque o e-mail é o seu login: ela confirma que é você quem está trocando.',
      organizacao:
        'A organização não se altera aqui: ela define de que lado do contrato você está. Se estiver errada, fale ' +
        'com o administrador.',
      salvar: 'Salvar',
      cancelar: 'Cancelar',
      naoSalvou: 'Não foi possível salvar os seus dados',
      salvos: 'Pronto. Seus dados foram atualizados.',
    },
  },
  /**
   * A recusa por lado do contrato, dita sem eufemismo.
   *
   * "Você não tem permissão" faz a pessoa pedir a permissão — e alguém do outro lado perde tempo
   * explicando que não é disso que se trata. O texto nomeia o motivo real: a fiscalização mede a
   * execução do contrato e vira glosa na fatura; quem é medido não a enxerga.
   */
  semAcesso: {
    titulo: 'Esta tela não é do seu perfil de acesso',
    ajuda:
      'A fiscalização do contrato — chamados, indicadores e fechamento do mês — é da equipe da Prefeitura, ' +
      'porque é ela que mede a execução e assina o termo de recebimento. Quem executa o contrato acompanha ' +
      'as solicitações de mudança, no menu "Mudanças". Se você acha que deveria ver esta tela, fale com o ' +
      'administrador do sistema.',
  },
  emConstrucao: {
    titulo: 'Esta parte do sistema ainda está sendo construída',
    ajuda:
      'O menu já mostra tudo o que o sistema vai ter, para você saber o que vem por aí. Esta tela ainda não ' +
      'foi entregue — por enquanto, o que está no ar é o módulo de solicitações de mudança.',
  },
  relatorio: {
    botao: 'Gerar relatório',
    ajuda:
      'Documento com os dados da solicitação, para imprimir ou salvar em PDF e anexar ao processo. Mantém o ' +
      'formato do sistema anterior de propósito: é o que a fiscalização e o comitê já conhecem.',
  },
  /**
   * Qual recorte a listagem está mostrando, escrito na própria tela.
   *
   * A lista mostra só o processo deste sistema por padrão: 1.025 dos 1.027 registros importados
   * estão como "Aprovado", e misturados eles tomam a primeira página e se leem como aprovações do
   * comitê, que nunca houve. Recorte que a tela não conta vira "sumiram as GMUDs antigas" no
   * suporte, e por isso o texto anda sempre com o caminho para o outro recorte ao lado.
   */
  recorteDaLista: {
    processo: 'Esta lista mostra só as solicitações abertas neste sistema.',
    verHistorico: 'Ver o histórico do sistema anterior',
    historico: 'Esta lista mostra só o histórico trazido do sistema anterior.',
    verProcesso: 'Ver as solicitações deste sistema',
    ambas: 'Esta lista mostra as duas procedências: o processo deste sistema e o histórico anterior.',
  },
  semResultados: {
    titulo: 'Nenhuma solicitação encontrada',
    // "Ver todas as solicitações" deixou de ser o que limpar os filtros faz: a lista limpa mostra
    // as deste sistema, e o histórico anterior fica no recorte próprio. Prometer "todas" mandaria
    // o usuário procurar um registro de 2019 numa tela onde ele não vai estar.
    ajuda: 'Nenhum registro atende aos filtros aplicados. Limpe os filtros para voltar à lista deste sistema.',
  },
  listaVazia: {
    titulo: 'Ainda não há solicitações de mudança',
    ajuda: 'Toda alteração que vai para produção começa aqui. Abra a primeira solicitação para começar.',
  },
  semAcoes: {
    titulo: 'Nenhuma ação disponível para você agora',
    ajuda:
      'Isto depende do estado da solicitação e do seu perfil. Se acredita que deveria poder agir, fale com a equipe da SSTD.',
  },
  semHistorico: 'Nenhuma movimentação registrada ainda.',

  /**
   * O carimbo de procedência das telas de chamado.
   *
   * Não é enfeite nem aviso amarelo: é o que impede a leitura errada mais provável do módulo. A
   * base do e-Niterói alcançável está congelada em 23/05/2026, e a planilha de maio traz 64 dos 91
   * chamados que a fiscalização considerou. Uma tela que mostra 64 linhas sem dizer até onde o dado
   * vai deixa qualquer leitor concluir que houve 64 chamados no contrato — e o número sai com
   * aparência de certo, que é o pior tipo de erro num sistema de comprovação contratual.
   *
   * Por isso o texto é permanente e não dispensável, e vem sempre acompanhado de uma data.
   */
  procedenciaDoDado: {
    rotulo: 'De onde vêm estes números',
    fonte: 'Estes chamados vieram de',
    ateAqui: 'O dado mais recente é de',
    alcance:
      'O que aconteceu depois dessa data ainda não está aqui. Esta tela mostra o que foi carregado, não tudo o ' +
      'que o contrato teve no período.',
    semDado: 'Nenhum chamado carregado ainda, então não há data de corte a informar.',
    comoAtualizar: 'Para trazer o que falta, carregue a planilha do mês.',
    recolher: 'Recolher esta explicação',
    expandir: 'Ver de onde vêm estes números',
    resumo: 'Dado até',
  },

  /**
   * A barra que mostra quanto do prazo já foi consumido.
   *
   * A ajuda não é acessório: ela é o que impede a barra de virar medida contratual. O TR conta o
   * SLA em HORAS ÚTEIS e manda o relógio parar enquanto o chamado está "Pendente" (ANEXO IV, p.90,
   * definições 7 e 8; nota 4a, p.161-162). A barra compara tempo corrido, que é o que o e-Niterói
   * desenha na tela dele — serve para o olho achar o chamado apertado, não para assinar glosa.
   */
  barraDeSla: {
    rotulo: 'Consumo do prazo',
    consumido: 'do prazo consumido',
    estourado: 'prazo estourado',
    ajuda:
      'A barra compara o tempo corrido entre a abertura do chamado e o prazo registrado no e-Niterói, do mesmo ' +
      'jeito que a tela do e-Niterói faz. É indicação visual para achar o que está apertado — a medida que vale ' +
      'no contrato é em horas úteis e desconta o tempo em "Pendente".',
  },

  /**
   * A tela em que o fiscal registra o julgamento dele sobre um chamado.
   *
   * Cada pergunta é feita pelo FATO, nunca pela negativa ("não é manutenção corretiva?"), e cada
   * ajuda ataca o erro concreto que a planilha de maio/2026 registrou. O público é fiscal de
   * contrato leigo em TI e o número que sai daqui vira glosa em documento assinado.
   */
  marcacao: {
    titulo: 'Registrar análise da fiscalização',
    abrir: 'Registrar análise',
    editar: 'Editar análise',
    salvar: 'Salvar análise',
    cancelar: 'Cancelar',
    salvando: 'Salvando…',
    naoSalvou: 'Não foi possível salvar a análise',
    /**
     * O 409: outra pessoa julgou o chamado enquanto este formulário estava aberto.
     *
     * A tela recarrega o julgamento vigente e o mostra ABAIXO do formulário, com o que a pessoa
     * digitou ainda em cima. Sem isso, "recarregue e tente de novo" só faria repetir a mesma
     * sobrescrita: o julgamento decide se o chamado entra em ICA, IAP, IAE e TIS, e a marcação não
     * guarda cópia do que havia antes.
     */
    conflito: {
      titulo: 'Alguém julgou este chamado enquanto você editava',
      vigente: 'O julgamento que está gravado agora',
      comoSeguir:
        'Confira o que está gravado abaixo antes de salvar de novo. Se o seu julgamento continua sendo o certo, ' +
        'clique em Salvar mais uma vez — agora o sistema já sabe qual versão você viu.',
    },
    semPapel:
      'Só quem tem atribuição de fiscalização registra a análise. Se você precisa fazer isso, peça o perfil de ' +
      'fiscal ao administrador do sistema.',
    /** O aviso mais importante da tela: salvar aqui muda quem manda naquele chamado. */
    avisoDePrecedencia:
      'Ao salvar, esta análise passa a valer sobre a do arquivo: as próximas cargas de planilha preservam o que ' +
      'você escreveu aqui e apenas relatam a diferença, em vez de sobrescrever.',
    /**
     * Em que mês esta análise vai contar — a pergunta que a tela precisa responder ANTES de salvar.
     *
     * Chamado ainda aberto não pertence a competência nenhuma: pelo critério em uso, a competência
     * é o mês do FECHAMENTO. Analisá-lo é legítimo e fica registrado, mas não move indicador algum
     * até ele fechar. Sem esta frase, o fiscal analisa dezenas de chamados abertos e vê o
     * fechamento do mês parado em zero, sem nada na tela explicando por quê.
     */
    ondeConta: {
      fechado: 'Esta análise conta no fechamento de',
      aberto:
        'Este chamado ainda está aberto, então não pertence a nenhum mês de referência. A análise fica registrada ' +
        'e passa a contar no mês em que ele for fechado.',
    },
    foraDaApuracao: {
      aviso:
        'Fora da apuração: este chamado não entra em nenhum indicador do mês (ICA, IAP, IAE nem TIS), e as ' +
        'quatro perguntas do julgamento não são feitas. Diga na observação por quê — categoria errada, ' +
        'desistência do usuário antes do atendimento, chamado duplicado.',
      semMotivo:
        'Diga, na observação, por que este chamado fica fora da apuração do mês. É o que a contratada lê se contestar.',
      leitura: 'Fora da apuração do mês',
    },
    contaNoMes: {
      rotulo: 'Este chamado entra na apuração do mês',
      ajuda:
        'O critério em uso é o mês em que o chamado FECHOU, não em que foi aberto — foi assim que o termo de ' +
        'recebimento de maio/2026 apurou 45 chamados, e não os 70 que foram abertos no período.',
    },
    natureza: {
      rotulo: 'Que serviço foi prestado?',
      ajuda:
        'Manutenção corretiva é o conserto de um defeito do sistema em produção; ela sai da conta de prazos e é ' +
        'medida pelo tempo de solução de incidentes. Todo o resto — dúvida, orientação, parametrização, melhoria — ' +
        'entra na conta de prazos. É uma coisa ou outra, nunca as duas.',
      corretiva: 'Manutenção corretiva — conserto de defeito em produção',
      naoCorretiva: 'Outro serviço — dúvida, orientação, parametrização, melhoria, adaptação',
    },
    prazo: {
      rotulo: 'O atendimento cumpriu o prazo?',
      ajuda:
        'Responda pelo prazo do serviço que REALMENTE foi prestado, não pelo que o e-Niterói calculou: o contrato ' +
        'permite reclassificar o serviço depois do diagnóstico, e o prazo muda junto. Quando discordar do ' +
        'e-Niterói, escreva o motivo na observação — em maio/2026 isso aconteceu em 8 dos 36 chamados.',
      noPrazo: 'Sim, dentro do prazo',
      foraDoPrazo: 'Não, fora do prazo',
      naoSeAplica: 'Não se aplica — não entra no IAP',
      naoSeAplicaAjuda:
        'Para chamado sem prazo a medir, como pedido de permissão de acesso ou chamado em que o SLA não carregou ' +
        'no e-Niterói. Ele sai do IAP (da conta e do total) e continua no mês para os outros indicadores.',
    },
    conformidade: {
      rotulo: 'A entrega teve inconformidade em algum artefato?',
      ajuda:
        'Vale qualquer artefato que faltou ou veio errado: evidência não anexada ao chamado, documento fora do ' +
        'modelo, GMUD não anexada. Basta um artefato irregular para a entrega inteira contar como inconforme.',
      conforme: 'Não, a entrega está conforme',
      inconforme: 'Sim, houve inconformidade',
    },
    aceite: {
      rotulo: 'Como o usuário recebeu a entrega?',
      ajuda:
        'O aceite parcial vale um terço do integral na fórmula, então a diferença entre os dois muda o indicador. ' +
        'Chamado fechado sem o usuário aprovar não é aceite integral.',
      integral: 'Aceitou integralmente',
      aposTentativas: 'Aceite após 3 tentativas de contato',
      parcial: 'Aceitou parcialmente',
      semAceite: 'Não aceitou',
      naoSeAplica: 'Não se aplica — não entra no IAE',
      aposTentativasAjuda:
        'Conta como aceite integral no IAE, mas fica registrado à parte: é o que mostra quantas entregas foram ' +
        'homologadas pelo próprio usuário e quantas não.',
    },
    criticidade: {
      rotulo: 'Criticidade do incidente',
      ajuda: 'Define o prazo de solução: alta 12 horas úteis, média 24, baixa 40 (TR, p.160).',
      naoClassificada: 'Não classificada',
    },
    /**
     * A sugestão da IA ao lado de cada pergunta.
     *
     * O rótulo diz "sugestão" e não "resposta" em todo lugar, e a tela sempre mostra o porquê: o
     * fiscal precisa conseguir discordar com a justificativa à vista, senão o campo pré-preenchido
     * vira uma caixa que se assina sem ler — que é exatamente o que o contrato não aceita.
     */
    sugestaoDaIa: {
      rotulo: 'Sugestão da IA',
      aceita: 'você manteve a sugestão',
      alterada: 'você alterou a sugestão',
      semProposta: 'A IA não sugeriu resposta para esta pergunta',
      confianca: { ALTA: 'confiança alta', MEDIA: 'confiança média', BAIXA: 'confiança baixa' },
      comoUsar:
        'As respostas sugeridas pela IA já vêm preenchidas e marcadas. Confira uma a uma e mude o que ' +
        'discordar — o que você salvar é o que vale, e fica registrado quais sugestões você manteve.',
      resumo: 'O que a IA sugeriu',
      usarTodas: 'Usar todas as sugestões',
      usarTodasAjuda: 'Preenche o formulário com as respostas acima. Nada é salvo até você conferir e clicar em Salvar.',
      trilha: 'Sugestões da IA mantidas neste registro:',
      trilhaNenhuma: 'Nenhuma sugestão da IA foi mantida — todas as respostas foram do fiscal.',
      semIa: 'Registrado sem apoio de IA.',
    },
    classificacaoServico: {
      rotulo: 'Serviço que de fato foi prestado',
      ajuda:
        'Preencha quando a categoria do e-Niterói estiver errada. O contrato autoriza a Prefeitura a reclassificar ' +
        'depois do diagnóstico, e as duas ficam registradas lado a lado.',
    },
    observacao: {
      rotulo: 'Observação',
      ajuda:
        'É o campo que sustenta a decisão quando alguém contestar o número. Obrigatório na prática sempre que sua ' +
        'análise divergir do que o e-Niterói calculou.',
    },
  },

  /** A tela de administração de contas e papéis. */
  usuarios: {
    titulo: 'Usuários e perfis',
    ajuda:
      'Quem entra no sistema e o que cada pessoa pode fazer. É aqui que se decide quem aprova mudança e quem ' +
      'julga chamado — as duas coisas viram documento assinado.',
    novaConta: {
      titulo: 'Cadastrar conta',
      ajuda:
        'A pessoa entra com o e-mail e a senha provisória que você definir aqui. No primeiro acesso o sistema ' +
        'obriga a troca da senha.',
    },
    organizacao:
      'Decide de que lado do contrato a pessoa está. Quem é da contratada não enxerga as telas de fiscalização, ' +
      'e não valida a própria execução.',
    senhaProvisoria:
      'Pelo menos 12 caracteres. São mais que os oito de costume porque esta senha é digitada por você e ' +
      'transmitida por mensagem até a pessoa trocá-la.',
    papeis:
      'Sem papel a pessoa entra e não vê nada. "Somente consulta" é o mínimo; os demais liberam ações e podem ' +
      'ser combinados.',
    semAdminNaContratada:
      'Administrador não aparece aqui porque esta conta é da empresa contratada. O administrador reabre mês ' +
      'assinado e fixa prazo de relatório — atos que recaem sobre ela própria, e por isso são do lado da ' +
      'Prefeitura.',
    alterarPapeis: {
      botao: 'Papéis',
      ajuda:
        'Marque tudo o que esta pessoa pode fazer. O que você deixar desmarcado é retirado — a lista abaixo ' +
        'passa a ser o conjunto completo dos papéis dela. O efeito vale já na próxima ação que ela fizer.',
      semPapelNenhum:
        'Sem nenhum papel marcado, a pessoa continua entrando no sistema e passa a só consultar. É o jeito ' +
        'de retirar acesso sem desativar a conta.',
    },
    naoCriou: 'Não foi possível cadastrar a conta',
    naoSalvou: 'Não foi possível salvar',
    listaVazia: {
      titulo: 'Nenhuma conta encontrada',
      ajuda: 'Nenhuma conta corresponde ao que você buscou. Limpe a busca para ver todas.',
    },
    trocaObrigatoria: {
      titulo: 'Troque sua senha para continuar',
      ajuda:
        'Sua senha foi definida por um administrador e chegou até você por um canal que ninguém controla — ' +
        'e-mail ou mensagem. Enquanto ela não for trocada, quem a viu no caminho entra na sua conta.',
      senhaAtual: 'Senha atual',
      senhaAtualAjuda: 'A que você acabou de usar para entrar. É pedida para confirmar que é você.',
      novaSenha: 'Nova senha',
      novaSenhaAjuda: 'Pelo menos 12 caracteres. Não repita a provisória.',
      confirmar: 'Repita a nova senha',
      naoConfere: 'As duas senhas não são iguais. Digite a mesma nos dois campos.',
      salvar: 'Trocar senha',
      naoTrocou: 'Não foi possível trocar a senha',
    },
  },

  /**
   * Quando o arredondamento — e não o desempenho — decide o desconto.
   *
   * O TR se contradiz: o item 8.13 (p.56) manda apresentar os decimais "com 2 casas sem
   * arredondamento" e as fichas do ANEXO VIII mandam "Arredondar sempre o %". Perto da borda de
   * uma faixa as duas leituras caem em faixas diferentes. A tela não esconde a escolha: mostra as
   * duas, diz qual está sendo aplicada e por quê.
   */
  enquadramento: {
    enquadradoComo: 'Enquadrado como',
    ajudaDoEnquadrado:
      'A tabela de descontos do contrato é escrita em degraus, e o valor apurado é encaixado no degrau mais ' +
      'próximo antes de virar percentual de desconto. É este número encaixado que decide o desconto, não o ' +
      'valor cheio.',
    tituloDaBorda: 'O arredondamento decidiu este desconto',
    ajudaDaBorda:
      'O Termo de Referência admite duas leituras do mesmo número — arredondar ou cortar as casas decimais — e ' +
      'aqui elas caem em degraus diferentes da tabela. O sistema aplica a leitura arredondada, que é a que o ' +
      'próprio TR pratica nos exemplos e a que resulta no menor desconto entre as duas.',
    aplicada: 'aplicada',
    arredondado: 'Arredondando',
    truncado: 'Cortando as casas decimais',
    semDesconto: 'sem desconto',
    ressalvaDoIap:
      'Atenção neste indicador: a tabela do IAP (p.152) é a única do ANEXO VIII que não traz a observação ' +
      '"Arredondar sempre o %". Se a fiscalização decidir tratá-lo de outro jeito, é o caso de registrar a ' +
      'decisão por escrito antes do fechamento.',
  },

  /**
   * As observações registradas numa solicitação.
   *
   * O rito do comitê acontece no escritório — reunião, pauta e ata são presenciais. O que fica no
   * sistema é a análise e o registro do que se observou nela.
   */
  notas: {
    titulo: 'Observações',
    ajuda:
      'O que se observou ao analisar esta solicitação: o que o parecer automático levantou e o comitê descartou, ' +
      'o que a revisão de código mostrou, o combinado da reunião. Não é decisão — a do comitê é a ação registrada ' +
      'acima, com fundamentação. As observações são lidas pelos DOIS lados do contrato — Prefeitura e ' +
      'contratada — e não podem ser apagadas.',
    campo: 'Nova observação',
    ajudaDoCampo: 'Escreva para quem vier depois e não estava na conversa.',
    salvar: 'Registrar observação',
    salvando: 'Registrando…',
    naoSalvou: 'Não foi possível registrar a observação',
    vazio: 'Nenhuma observação registrada ainda.',
    imutavel: 'Observação registrada não pode ser editada nem apagada.',
  },

  /** A janela do comitê, vinda do servidor porque já mudou uma vez. */
  janelaDoComite: {
    rotulo: 'Próxima janela de implantação',
    analiseNos: 'O comitê analisa às',
    aPartirDe: 'e o aprovado pode ir a produção a partir das',
    abertaAgora: 'A janela está aberta agora.',
    ajuda:
      'Implantar fora da janela não é impedido pelo sistema: é fato contratual que a fiscalização registra e que ' +
      'pode virar ponto administrativo. Recusar a operação esconderia o achado em vez de documentá-lo.',
  },

  /**
   * Os alertas que a análise automática levanta sobre um chamado.
   *
   * O rótulo é escrito para quem vai conferir, não para quem escreveu o prompt: "Pendente usado
   * para parar o relógio" diz o que procurar; "PENDENTE_ESTACIONAMENTO" não diz nada a ninguém.
   */
  alertasDaIa: {
    rotulo: 'O que a análise apontou',
    ajuda:
      'Filtra os chamados em que a leitura automática levantou aquele ponto. É apoio à conferência — nada aqui ' +
      'entra em indicador sem o seu julgamento.',
    todos: 'Qualquer ponto',
    codigos: {
      PENDENTE_ESTACIONAMENTO: 'Pendente usado para parar o relógio',
      FECHAMENTO_NA_BORDA: 'Fechado em cima do prazo',
      CATEGORIA_DIVERGENTE: 'Categoria parece trocada',
      SEM_PRAZO_SUSPEITO: 'Sem prazo, sem motivo aparente',
      SEM_PRIMEIRA_INTERACAO: 'Sem primeira resposta registrada',
      RESOLVIDO_MUITO_ANTES_DO_FECHAMENTO: 'Resolvido muito antes de fechar',
      PAI_FRACIONADO: 'Mesma causa dividida em vários chamados',
      DADO_INCONSISTENTE: 'Dado inconsistente na origem',
      OUTRO: 'Outro ponto',
    },
    nenhum:
      'A leitura automática não apontou nada nos chamados que ela leu. Isso não quer dizer que o mês está ' +
      'limpo: veja acima quantos chamados foram lidos.',
  },

  /**
   * A cobertura da análise — o denominador que faltava.
   *
   * Sem ele, "2 chamados com Pendente usado para parar o relógio" sobre 3 lidos de 105 lê-se como
   * retrato do mês. É amostra, e a tela tem de dizer isso ANTES de mostrar a lista.
   */
  coberturaDaAnalise: {
    rotulo: 'Quanto do mês foi lido pela análise automática',
    contagem: (analisados: number, total: number) =>
      `${analisados} de ${total} chamados deste mês foram lidos pela análise automática.`,
    amostra:
      'Os pontos listados abaixo saíram apenas desses chamados. Eles não são um retrato do mês inteiro — ' +
      'um problema que não aparece aqui pode existir num chamado que ainda não foi lido.',
    nenhum:
      'Nenhum chamado deste mês foi lido pela análise automática ainda. A lista de pontos está vazia por ' +
      'isso, e não porque o mês esteja limpo.',
    completa: 'Todos os chamados fechados deste mês foram lidos.',
  },

  /**
   * As suspeitas de fracionamento. NÃO é texto de IA: é uma consulta.
   *
   * Por isso não leva selo de máquina e o texto fala em "coincidência", nunca em "mesma causa".
   * "Chamado-pai" é termo do contrato e não se traduz — ganha ajuda contextual (Regra nº 4).
   */
  suspeitasDeFracionamento: {
    titulo: 'Chamados que podem ser o mesmo problema',
    ajuda:
      'Pelo contrato, chamados com a mesma causa contam como UM (ANEXO IV, definição 2, página 89). ' +
      'Quando o mesmo problema é aberto várias vezes, o total do mês cresce e os percentuais ficam ' +
      'melhores do que deveriam. Abaixo estão os chamados fechados que coincidem em órgão, categoria e ' +
      'semana de abertura. É uma coincidência contada pelo sistema, não uma conclusão: só você, abrindo ' +
      'os chamados, pode dizer se a causa é a mesma.',
    ajudaChamadoPai:
      'Chamado-pai é o primeiro chamado de uma mesma causa. Os outros são reclassificados como ligados a ' +
      'ele, e o conjunto passa a contar como um só atendimento.',
    grupo: (quantos: number) => `${quantos} chamados`,
    semana: (semana: string) => `semana ${semana}`,
    vazio: 'Nenhum grupo de chamados coincidiu em órgão, categoria e semana neste mês.',
    naoAgrupa:
      'Nada aqui altera classificação de chamado. Se você concordar com um grupo, a reclassificação é ' +
      'feita por você, chamado a chamado, e fica registrada com o seu nome.',
  },

  /**
   * A conferência do mês na tela de fechamento.
   *
   * A ajuda diz "antes de assinar" e não "sobre o mês": é o momento da decisão que define o texto.
   * E o limite aparece na primeira frase, não no rodapé — quem lê rápido lê o começo.
   *
   * Nenhuma das frases de estado é uma mensagem de erro, e nenhuma pede que se espere: assinar o
   * mês não depende deste texto. O único dano que esta tela pode causar sozinha é fazer o fiscal
   * hesitar diante de um número correto, ou adiar o fechamento esperando um parecer que talvez
   * nunca chegue.
   */
  conferenciaDoMes: {
    titulo: 'O que conferir antes de fechar o mês',
    ajuda:
      'Leitura automática desta apuração, feita por inteligência artificial, apontando o que merece ' +
      'conferência antes de você assinar. Ela não calcula nenhum indicador e não impede o fechamento — ' +
      'os números acima são do sistema, e quem decide fechar é você.',
    itens: 'O que conferir',
    comoConferir: 'O que conferir:',
    botao: 'Fazer a leitura do mês',
    refazer: 'Refazer a leitura',
    preparando:
      'Estamos preparando a leitura deste mês. Ela aparece aqui sozinha, em instantes. Você não precisa ' +
      'esperar por ela para fechar o mês.',
    naoPedido:
      'A leitura deste mês ainda não foi feita. Ela é preparada sozinha quando a apuração muda — depois ' +
      'de você julgar chamados, por exemplo. Isto não é erro e não impede nada; se preferir não esperar, ' +
      'peça a leitura agora.',
    desatualizado:
      'A apuração mudou depois desta leitura. O texto abaixo pode não refletir os números atuais — peça ' +
      'uma leitura nova antes de assinar.',
    nadaAConferir:
      'A leitura não encontrou nada para conferir além dos números já apresentados. Isso não é uma ' +
      'aprovação do mês: quem confere e assina é você.',
    falhou:
      'A leitura automática não foi concluída desta vez. Isto é do serviço de inteligência artificial, ' +
      'não da sua apuração — os números acima não dependem dele e continuam valendo.',
    indisponivel:
      'A leitura automática está indisponível no momento. Os números acima não dependem dela.',
    noTeto:
      'A leitura automática atingiu o limite de uso deste mês, então ela não é preparada sozinha por ' +
      'enquanto. Você ainda pode pedir uma leitura pelo botão, e nada aqui impede fechar o mês.',
    // Estado próprio, e não `noTeto` com outro texto: aquele promete o botão, e com o crédito
    // encerrado o botão também não vai. E sem número nem dinheiro: quem lê esta frase é o fiscal,
    // no momento em que ele decide assinar o mês.
    semCredito:
      'A leitura por inteligência artificial está pausada até a virada do mês. Isso não muda nada aqui: ' +
      'os números acima são do sistema, e o fechamento segue normalmente. A administração já foi ' +
      'avisada.',
    codigos: {
      CHAMADOS_SEM_JULGAMENTO: 'Chamados fechados que ninguém julgou',
      COBERTURA_BAIXA_DA_ANALISE: 'Poucos chamados foram lidos pela análise',
      INDICADOR_NAO_APURADO: 'Indicador sem número neste mês',
      INDICADOR_NA_BORDA: 'Indicador em cima do limite da faixa',
      REDUTOR_NAO_DEFINITIVO: 'O desconto ainda pode mudar',
      TETO_DE_REDUTOR_ALCANCADO: 'O desconto bateu no limite de 10%',
      CORRETIVO_SEM_CRITICIDADE: 'Conserto sem criticidade definida',
      DISPENSA_SEM_FUNDAMENTO: 'Dispensa de desconto sem justificativa escrita',
      VALOR_NAO_REGISTRADO: 'Valor da nota ainda não registrado',
      SEM_ORDEM_DE_SERVICO: 'Mês sem ordem de serviço registrada',
      SUSPEITA_DE_FRACIONAMENTO: 'Chamados que podem ser o mesmo problema',
      COBERTURA_DO_DADO_INCOMPLETA: 'A apuração não alcança o fim do mês',
      DIVERGENCIA_ENTRE_CONTAGENS: 'Contagens que não fecham entre si',
      PRIMEIRA_COMPETENCIA: 'Primeiro mês trabalhado dentro do sistema',
      OUTRO: 'Outro ponto',
    } as Record<string, string>,
  },

  /**
   * A procedência do texto de máquina, num lugar só.
   *
   * A mesma frase estava escrita duas vezes — no apoio do chamado e no painel do comitê —, e a
   * segunda cópia é onde a divergência começa: bastaria alguém melhorar uma para o sistema passar a
   * dizer duas coisas diferentes sobre o que um parecer de IA é.
   */
  notaDoTis: {
    titulo: 'O que está acima é a marcação do fiscal, não o TIS apurado.',
    ajuda:
      'O TIS é apurado no fechamento do mês, não aqui. A criticidade que vale é a marcada pelo fiscal; sem ' +
      'marcação, vale a da categoria do e-Niterói, que fica gravada ao lado. O prazo conta em horas úteis, ' +
      'e quando o mês não puder ser medido o cartão do TIS no fechamento diz por quê.',
  },
  seloDeMaquina: {
    rotulo: 'Leitura automática',
    frase: (modelo: string, quando: string) =>
      `Texto escrito por um programa de inteligência artificial (${modelo}) em ${quando}. ` +
      'Não é decisão nem autorização — quem decide e assina é a fiscalização. ' +
      'Nenhum número desta tela foi calculado por ele.',
    semModelo: 'modelo não informado',
  },

  /**
   * O "não é isso" da fiscalização.
   *
   * Discordar não apaga o ponto nem regenera o parecer, e o texto diz isso: o registro existe para
   * o próximo leitor, que sem ele releria um ponto já refutado como se fosse novo.
   */
  discordancia: {
    botao: 'Não é isso',
    titulo: 'Discordar deste ponto',
    ajuda:
      'Escreva por que este ponto não procede. O texto fica guardado junto do parecer, com o seu nome e a ' +
      'data, e aparece para quem ler depois. Nada é apagado, nada é reescrito, e o parecer não muda.',
    campo: 'Por que este ponto não procede',
    curto: 'Escreva o motivo — pelo menos uma frase.',
    salvar: 'Registrar discordância',
    marca: 'A fiscalização discordou deste ponto',
    autoria: (nome: string, quando: string) => `${nome}, em ${quando}`,
  },

  chamados: {
    atualizacaoParada: {
      titulo: (quando: string) => `Este chamado não é atualizado pelo e-Niterói desde ${quando}`,
      ajuda:
        'O estado mostrado aqui pode estar desatualizado. A causa mais comum: o chamado foi transferido para um ' +
        'grupo de especialistas da contratada (RH, Tributário, Financeiro, Patrimonial ou Portal Transparência) do ' +
        'qual a conta de integração não é membro, e a busca deixou de enxergá-lo. Confira no e-Niterói antes de ' +
        'julgar. A correção é incluir a conta de integração nesses grupos, no e-Niterói.',
    },
    ajuda:
      'Os atendimentos do e-Niterói que são objeto de fiscalização do contrato. O mês de referência de um ' +
      'chamado é o do fechamento dele.',
    descricaoDaLista: 'Chamados que atendem aos filtros',
    itensPorPagina: 'Chamados por página',
    listaVazia: {
      titulo: 'Ainda não há chamados carregados',
      ajuda:
        'A fiscalização recebe da contratada a planilha dos chamados do mês e a carrega aqui. Enquanto isso não ' +
        'acontece, o sistema não tem como saber quais chamados existiram — e não vai inventar nenhum.',
    },
    semResultados: {
      titulo: 'Nenhum chamado encontrado',
      ajuda:
        'Nenhum chamado atende aos filtros aplicados. Limpe os filtros para voltar à lista inteira. Lembre-se de ' +
        'que o mês de referência é o do fechamento: um chamado aberto em maio e fechado em junho é de junho.',
    },
    semVinculoComGmud: 'Nenhuma solicitação de mudança vinculada a este chamado.',

    // A coluna e o chip da fila de julgamento. "Sem julgamento" e não "Pendente": pendente é palavra
    // do e-Niterói e já significa outra coisa neste sistema (o status que para o relógio do prazo).
    semJulgamento: 'Sem julgamento',
    julgado: 'Julgado',
    colunaDoJulgamento: 'Análise do fiscal',
    semJulgamentoAjuda:
      'Chamados que ainda não passaram pela análise da fiscalização. Enquanto sobrar um deles no mês, a ' +
      'competência não fecha — e eles não entram em indicador nenhum, nem como conformes nem como ' +
      'irregulares. É por esta lista que se trabalha o mês.',
    julgamentoAusente: {
      titulo: 'A fiscalização ainda não analisou este chamado',
      ajuda:
        'Sem a análise do fiscal, ele não entra em indicador nenhum — nem como conforme, nem como irregular. ' +
        'Ficar de fora não é o mesmo que estar em dia.',
    },
  },

  /**
   * A tela que sustenta a assinatura do termo de recebimento.
   *
   * `naoApurado` é a frase que substitui o número, nunca um zero. Zero é o valor de quem cumpriu a
   * meta: três indicadores zerados em silêncio fariam a tela dizer "sem desconto" sobre metade do
   * anexo que ninguém olhou.
   */
  fiscalizacao: {
    /**
     * Prévia e fechamento têm a mesma aparência, e é isso que precisa deixar de ser verdade.
     *
     * Um indicador de 93,33% em preparação quer dizer "é quanto daria se o mês acabasse agora";
     * o mesmo número numa competência fechada quer dizer "é quanto vale, e foi isto que foi
     * assinado". Sem o selo, os dois se leem igual.
     */
    selo: {
      emPreparacao: 'Prévia — este mês ainda está em preparação',
      emPreparacaoAjuda:
        'Os números mudam a cada chamado que você julga. Enquanto o mês não é fechado, isto é uma ' +
        'estimativa do que daria se ele acabasse agora — não é o número do termo de recebimento.',
      fechada: 'Mês fechado — estes números estão congelados',
      fechadaAjuda:
        'A apuração foi registrada como está e não muda mais: é ela que sustenta o termo de recebimento. ' +
        'Julgamento novo neste mês é recusado; para corrigir, um administrador precisa reabrir.',
      reaberta: 'Reaberta em',
    },
    acoes: {
      fechar: 'Fechar o mês',
      fechando: 'Fechando…',
      fecharAjuda:
        'Fechar congela a apuração como ela está agora: é este número que sustenta o termo de recebimento. ' +
        'A partir daqui, julgamento novo neste mês passa a ser recusado — se você ainda está conferindo ' +
        'algum chamado, termine antes. Dá para desfazer: um administrador reabre o mês, e a reabertura ' +
        'fica registrada com o motivo.',
      mesEmCurso: (mes: string) =>
        `${mes} ainda está em curso. O mês só pode ser fechado a partir do dia 1º do mês seguinte, quando ` +
        'todos os chamados dele já tiverem fechado.',
      reabrir: 'Reabrir o mês',
      reabrirAjuda:
        'Reabrir permite corrigir e apurar de novo. O fechamento anterior NÃO é apagado: a apuração ' +
        'nova fica ao lado dele, e quem conferir o processo vê que houve reabertura, por quem e por quê.',
      faltaJulgar: (quantos: number) =>
        `Ainda ${quantos === 1 ? 'há 1 chamado fechado neste mês sem o seu julgamento' : `há ${quantos} chamados fechados neste mês sem o seu julgamento`}. ` +
        'Sem ele, esses chamados não entram na conta e os indicadores saem melhores do que a realidade.',
      naoDeuCerto: 'Não foi possível concluir',
    },
    /**
     * A dispensa do ANEXO VIII item d (p.148) — o registro que faltava.
     *
     * É ela que concilia os dois documentos de maio/2026: o TRP apurou glosa de R$ 18.287,04 e a
     * planilha da fiscalização registra R$ 0,00, com a observação de que maio foi período de
     * adaptação. Nenhum dos dois está errado; o ato não estava registrado em lugar nenhum.
     */
    dispensa: {
      selo: 'Sanções dispensadas neste mês',
      registrar: 'Dispensar sanções do mês',
      revogar: 'Revogar a dispensa',
      ajuda:
        'O contrato permite dispensar as sanções por indicador nos trinta primeiros dias, como período ' +
        'de ajuste, a critério da Prefeitura e desde que a contratada demonstre que os trabalhos estão ' +
        'em andamento (Anexo VIII, item d). Escreva o fundamento: é ele que sustenta o ato, e é o que ' +
        'alguém vai ler no processo. O desconto apurado continua visível — o que vai a zero é o aplicado.',
    },
    ajuda:
      'Os indicadores do contrato apurados no mês, cada um com a conta que o produziu e a lista de chamados que ' +
      'entraram nela. É o que permite conferir o número antes de assinar, em vez de acreditar nele.',
    escolherCompetencia: 'Mês de referência',
    ajudaDaCompetencia: 'Só aparecem os meses que já têm chamado fechado carregado.',
    semCompetencias: {
      titulo: 'Nenhum mês tem dado carregado',
      ajuda:
        'O fechamento é apurado sobre os chamados fechados no mês. Carregue a planilha da fiscalização do mês ' +
        'para que os indicadores possam ser calculados.',
    },
    coberturaIncompleta:
      'A coleta deste mês não alcança o fim dele. O que está apurado aqui é um mês incompleto: use com essa ' +
      'ressalva e não assine o fechamento antes de carregar o restante.',
    naoApurado: 'Não apurado',
    saida: {
      iad: 'Conferir as ocorrências do mês nos pontos administrativos',
      calendario: 'A conferência dos dias não úteis é feita por um administrador, em Parâmetros e listas de apoio.',
    },
    naoApuradoAjuda:
      'Sem número não é zero. Zero seria o resultado de quem cumpriu a meta; aqui é o resultado de quem ainda ' +
      'não tem como medir, e por isso este indicador fica de fora da soma do desconto.',
    denominador: 'Sobre',
    verComposicao: 'Ver os chamados que entram nesta conta',
    foraDaApuracao: {
      titulo: 'Chamados que você tirou da apuração',
      ajuda:
        'Fecharam no mês e foram julgados como fora da apuração. Não entram em denominador nenhum — ICA, IAP, ' +
        'IAE e TIS — e ficam listados aqui com o motivo, para a conta poder ser conferida.',
      semMotivo: 'sem motivo registrado',
    },
    semJulgamento: {
      titulo: 'Chamados fechados no mês que ninguém julgou',
      ajuda:
        'Estes chamados fecharam no mês de referência e não têm análise da fiscalização, então ficaram fora de ' +
        'todos os denominadores acima. Estão nomeados um a um porque sumir com eles em silêncio é como um mês ' +
        'perde chamado sem ninguém ver. Cada número abre o chamado; o link abaixo abre a lista inteira, que é ' +
        'onde dá para trabalhar a fila.',
      verAFila: 'Abrir a lista dos que faltam julgar',
    },
    tetoAlcancado: 'A soma passou do limite do contrato. O que vale é o limite, não a soma.',
    semDispensa:
      'Não há dispensa de sanção registrada para este mês: o desconto apurado é o que incide na nota. A ' +
      'dispensa, quando cabe, é registrada nos atos do mês, no alto desta tela.',
  },

  /**
   * A carga da planilha.
   *
   * `jaImportado` não é erro e não pode parecer um: reenviar o mesmo arquivo é o que acontece quando
   * alguém não tem certeza se já carregou. O que não pode é falhar em silêncio nem dizer "importado
   * com sucesso" sobre uma carga que não gravou nada.
   */
  importacao: {
    // "Manualmente" no rótulo: os chamados chegam sozinhos pela integração com o e-Niterói, e o
    // fiscal leu "Carregar planilha" como o caminho normal — e tentou a planilha de faturamento da
    // contratada, que não é este modelo (teste de uso 3, 25/09/2026).
    botao: 'Importar planilha manualmente',
    titulo: 'Importar a planilha da fiscalização',
    descricao:
      'Os chamados chegam sozinhos do e-Niterói, a cada 30 minutos. Use esta importação só quando a integração ' +
      'falhar, ou para trazer o julgamento de um mês feito fora do sistema.',
    modelo:
      'O modelo é a planilha de análise da fiscalização (como a de maio/2026): uma aba de fechados e uma de ' +
      'pendentes, com as colunas "ID" e "Data de abertura". A planilha de faturamento da contratada não serve — ' +
      'ela não traz os chamados nesse formato. Só arquivo .xlsx.',
    arquivo: 'Arquivo da planilha',
    ajudaDoArquivo:
      'O mês de referência sai do nome do arquivo quando ele traz "Ref AAAA-MM". Informe abaixo se quiser ' +
      'declará-lo — divergindo do nome, o arquivo é recusado inteiro.',
    ajudaDaCompetencia: 'Opcional, no formato AAAA-MM. Em branco, vale o que estiver no nome do arquivo.',
    enviar: 'Enviar',
    enviando: 'Enviando…',
    faltaArquivo: 'Escolha o arquivo da planilha antes de enviar.',
    resultado: 'Resultado da carga',
    linhasLidas: 'Linhas de dado lidas',
    novos: 'Chamados novos',
    alterados: 'Chamados atualizados',
    semMudanca: 'Vieram sem alteração',
    recusados: 'Linhas recusadas',
    recusas: 'O que não entrou, e por quê',
    avisos: 'O que entrou com ressalva',
    ignoradas: 'Linhas que não eram dado',
    historico: 'Cargas anteriores',
    semHistorico: 'Nenhuma planilha carregada ainda.',
  },

  /**
   * A carga periódica pela API do e-Niterói.
   *
   * "Atualização automática", nunca "sincronização GLPI": o público não sabe (nem precisa
   * saber) o que é GLPI. A planilha mensal continua sendo o documento oficial do processo —
   * o texto de ajuda diz isso porque a diferença importa para quem assina o termo.
   */
  sincronizacao: {
    titulo: 'Atualização automática do e-Niterói',
    ajuda:
      'O sistema busca os chamados direto no e-Niterói, de meia em meia hora, para você acompanhar o mês ' +
      'enquanto ele corre. A planilha mensal da contratada continua valendo como documento oficial.',
    botao: 'Buscar atualizações agora',
    buscando: 'Buscando…',
    indisponivelTitulo: 'A atualização automática ainda não está ligada',
  },

  /**
   * O apoio de IA à fiscalização.
   *
   * A ajuda repete, em linguagem de gente, a Regra nº 3: a IA aponta, quem decide é a
   * fiscalização. Esse limite não é detalhe técnico — é o que permite confiar no módulo.
   */
  /**
   * O calendário que mede o SLA: o do contrato e o da ferramenta.
   *
   * A tela precisa dizer isto em português de gente: "horas úteis" é jargão, e o fiscal
   * leigo não tem por que saber que existem dois calendários — mas precisa saber quando
   * eles discordam, porque é disso que sai a contestação da contratada.
   */
  calendario: {
    titulo: 'Calendário que conta os prazos',
    ajuda:
      'Os prazos deste contrato são contados em horas de expediente: o relógio anda só nos dias e ' +
      'horários úteis. Aqui você confere se o calendário usado nesta apuração é o mesmo que o ' +
      'e-Niterói usou para carimbar o prazo em cada chamado.',
    jornadaDoContrato: 'Expediente usado nesta apuração',
    jornadaDoENiteroi: 'Expediente cadastrado no e-Niterói',
    jornadaIgual: 'Os dois lados usam o mesmo expediente.',
    jornadaDivergente:
      'Os dois lados usam expedientes diferentes. Enquanto isso durar, o prazo que o e-Niterói mostra ' +
      'ao atendente e o que este sistema apura podem discordar sobre o mesmo chamado.',
    diasNoContrato: 'Dias sem expediente cadastrados aqui',
    diasNoENiteroi: 'Dias sem expediente no e-Niterói',
    naoConferidos: 'sem conferência de ninguém',
    semDivergencia: 'Nenhuma diferença de datas no período cadastrado.',
    divergencias: 'Datas em que os dois calendários discordam',
    soNoContrato: 'Só aqui',
    soNoENiteroi: 'Só no e-Niterói',
    indisponivel: 'Não foi possível comparar com o e-Niterói',
    relogio: 'Relógio usado para ler as datas do e-Niterói',
    relogioCerto: 'O sistema lê as datas do e-Niterói no relógio certo.',
    comoUsar:
      'Diferença de calendário não é erro do sistema nem da contratada: é divergência de cadastro. ' +
      'Ela fica registrada para a fiscalização decidir, com o Termo de Referência na mão, qual ' +
      'calendário vale — nada é corrigido automaticamente.',
  },

  analiseIa: {
    titulo: 'Apoio da inteligência artificial',
    ajuda:
      'Leitura automática do chamado que aponta o que merece conferência: prazo vencendo na borda, categoria ' +
      'possivelmente trocada, uso indevido de "pendente". É apoio — quem confere e decide é sempre a fiscalização.',
    botao: 'Pedir análise',
    analisando: 'Analisando…',
    semParecer: 'Nenhuma análise feita para este chamado ainda.',
    semAlertas: 'Nada chamou atenção nesta análise.',
    alertas: 'O que conferir',
    comoConferir: 'Como conferir:',
    falhou: 'A análise não foi concluída',
    indisponivelTitulo: 'A análise por IA não está configurada neste ambiente',
    refazer: 'Pedir uma leitura nova',
    /**
     * Os sete estados da leitura automática.
     *
     * Antes havia um texto só — "Nenhuma análise feita para este chamado ainda" — para cinco
     * situações diferentes. Quem lia não sabia se valia esperar, e a resposta certa é diferente em
     * cada uma: em PREPARANDO vale, em NO_TETO não vale (mas o botão passa), em INDISPONIVEL não
     * há o que esperar. Nenhum destes estados impede julgar o chamado.
     */
    estado: {
      PREPARANDO:
        'Estamos preparando a leitura deste chamado. Ela aparece aqui sozinha, em instantes. Você não ' +
        'precisa esperar por ela para julgar.',
      DESATUALIZADO:
        'O chamado mudou depois desta leitura. O texto abaixo pode não refletir o dado atual — peça uma ' +
        'leitura nova antes de usá-la como apoio.',
      NAO_PEDIDO: 'Nenhuma leitura foi feita para este chamado ainda. Isto não é erro e não impede nada.',
      NO_TETO:
        'A leitura automática atingiu o limite de uso deste mês, então ela não é preparada sozinha por ' +
        'enquanto. Você ainda pode pedir uma leitura pelo botão, e nada aqui impede julgar ou fechar.',
      // Sem R$, sem número, sem "crédito", sem "orçamento", sem "custo" e sem "teto": quem lê esta
      // frase é o fiscal, e nenhuma dessas palavras o ajuda a fazer o trabalho dele. O que ele
      // precisa saber é que a leitura parou, que isso não é problema dele, e que já avisamos quem
      // resolve. `glossario.test.ts` cobra isso por regex.
      SEM_CREDITO:
        'A leitura por inteligência artificial está pausada até a virada do mês. Isso não afeta o seu ' +
        'trabalho: os números desta tela são do sistema, e o julgamento segue normalmente. A ' +
        'administração já foi avisada; se você precisar de uma leitura agora, fale com ela.',
      INDISPONIVEL:
        'A leitura automática está indisponível no momento. Isto é do serviço de inteligência artificial, ' +
        'não do seu chamado — nada aqui depende dele.',
      FALHOU:
        'A leitura automática não foi concluída desta vez. Isto é do serviço de inteligência artificial, ' +
        'não do seu chamado.',
    } as const,
  },

  /**
   * O apoio ao comitê na tela da GMUD: revisão de código importada do GitHub e o
   * parecer de IA com recomendação. A ajuda repete o limite que importa: recomendação
   * não é decisão — quem aprova é o comitê, e é isso que sustenta o processo.
   */
  apoioAoComite: {
    parecerDefasado:
      'Chegou código novo depois deste parecer: há revisão ou merge de PR mais recente que a análise. O parecer ' +
      'abaixo pode não cobrir a última versão — peça uma nova análise antes de decidir.',
    pedirNovaAnalise: 'Analisar a versão atual',
    titulo: 'Apoio à decisão do comitê',
    ajuda:
      'Leitura automática da solicitação — plano, risco, janela, a revisão de código feita pelo GitHub ' +
      'Copilot nos PRs vinculados e os commits que já entraram na branch principal citando esta solicitação — ' +
      'com uma recomendação fundamentada no contrato. No reenvio, ela responde ao que o comitê pediu. É apoio: ' +
      'quem aprova ou reprova é sempre o comitê.',
    botao: 'Pedir parecer',
    analisando: 'Analisando…',
    semParecer: 'Nenhum parecer emitido para esta solicitação ainda.',
    ressalvas: 'O que conferir antes de decidir',
    semRessalvas: 'Nenhuma ressalva nesta análise.',
    comoConferir: 'O que exigir:',
    falhou: 'O parecer não foi concluído',
    recomendacao: {
      RECOMENDA_APROVAR: 'Recomenda aprovar',
      APROVAR_COM_RESSALVAS: 'Aprovar com ressalvas',
      RECOMENDA_REPROVAR: 'Recomenda reprovar',
    },
    revisaoDeCodigo: 'Análise de código (GitHub)',
    revisaoAjuda:
      'PRs no GitHub que citam o código desta solicitação no título, com a revisão automática do Copilot. ' +
      'A revisão é insumo do comitê — não aprova nada sozinha.',
    semRevisao: 'Nenhum PR citando esta solicitação foi encontrado ainda.',
    semRevisaoDoCopilot: 'PR sem revisão do Copilot até agora.',
    mesclado: 'Mesclado',
  },

  /** A tela de atividade nos repositórios do e-Cidade (V44). */
  repositorios: {
    titulo: 'Atividade nos repositórios',
    ajuda:
      'O que entrou na branch principal dos repositórios do e-Cidade, mês a mês: quem fez o quê, por ' +
      'setor e por pessoa. Evidência para a fiscalização e para o comitê — nada aqui muda solicitação, ' +
      'chamado ou apuração.',
    ateOntem: (ate: string) => `Cobre até ${ate}. O dia de hoje entra amanhã.`,
    mesFechado: (inicio: string, ate: string) => `Mês fechado: cobre de ${inicio} a ${ate}.`,
    mesComecouHoje: 'O mês começou hoje. O primeiro dia coberto aparece amanhã, quando hoje já tiver virado ontem.',
    mesAnterior: 'Mês anterior',
    mesSeguinte: 'Mês seguinte',
    escolherMes: 'Mês',
    escolherMesAjuda: 'Só aparecem os meses com commit coletado, mais o mês corrente.',
    semMeses: {
      titulo: 'Nenhum commit coletado ainda',
      ajuda:
        'A coleta ainda não trouxe nada. Veja a situação da coleta: pode ser que a credencial do GitHub ' +
        'não esteja configurada, ou que a primeira rodada ainda esteja em andamento.',
    },
    abas: {
      visao: 'Visão do mês',
      pessoas: 'Por pessoa',
      commits: 'Commits',
      repositorios: 'Repositórios e coleta',
    },
    numeros: {
      commits: 'Commits de trabalho',
      commitsAjuda: 'Sem os de integração (merge).',
      integracoes: 'Integrações',
      integracoesAjuda: 'Commits de merge: dizem quem integrou, não quem escreveu.',
      autores: 'Pessoas',
      autoresAjuda: 'Pessoas distintas com commit de trabalho no período.',
      repositorios: 'Repositórios',
      repositoriosAjuda: 'Repositórios com atividade no período.',
      comSolicitacao: 'Citam solicitação',
      comSolicitacaoAjuda: 'Commits de trabalho cuja mensagem cita o código de uma solicitação de mudança que existe aqui.',
      semSolicitacao: 'Sem solicitação citada',
      semSolicitacaoAjuda:
        'Commits de trabalho sem código de solicitação na mensagem. É o que o fiscal precisa conferir — ' +
        'não é, por si, infração: pode haver solicitação sem o código na mensagem.',
      linhas: 'Linhas alteradas',
      linhasAjuda: (semDetalhe: number) =>
        semDetalhe > 0
          ? `Acrescentadas e removidas. ${semDetalhe} ${semDetalhe === 1 ? 'commit ainda não tem' : 'commits ainda não têm'} arquivos e linhas medidos.`
          : 'Acrescentadas e removidas, somando os commits de trabalho.',
    },
    equipeInterna: {
      chip: 'equipe interna',
      aviso: (commits: number, pessoas: number) =>
        `${commits} ${commits === 1 ? 'commit' : 'commits'} de ${pessoas} ${pessoas === 1 ? 'pessoa' : 'pessoas'} da equipe interna ${commits === 1 ? 'fica' : 'ficam'} fora desta conta.`,
      verCommits: 'Ver esses commits',
      filtro: 'Equipe interna',
      foraDoRelatorio: 'Fora do relatório (padrão)',
      soAEquipe: 'Só a equipe interna',
    },
    secoes: {
      resumo: 'Resumo do mês',
      setores: 'Por setor',
      dias: 'Por dia',
      solicitacoes: 'Solicitações citadas',
      pessoas: 'Por pessoa',
      repositorios: 'Por repositório',
      coleta: 'Coleta',
      modificado: 'O que foi modificado',
    },
    modificado: {
      ajuda:
        'Onde se mexeu no e-Cidade no período: as áreas do código e os arquivos mais tocados, contando só ' +
        'commits de trabalho de quem não é da equipe interna.',
      areas: 'Áreas mais tocadas',
      arquivos: 'Arquivos mais tocados',
      colunas: { area: 'Área', commits: 'Commits', pessoas: 'Pessoas', arquivos: 'Arquivos', caminho: 'Arquivo' },
      semDetalhe: {
        titulo: 'Ainda não há arquivo medido neste período',
        ajuda: (semDetalhe: number) =>
          semDetalhe > 0
            ? `${semDetalhe} ${semDetalhe === 1 ? 'commit ainda não teve' : 'commits ainda não tiveram'} os arquivos lidos do GitHub. A próxima coleta completa.`
            : 'Nenhum commit de trabalho com arquivos no período.',
      },
    },
    resumo: {
      titulo: 'Resumo do mês',
      ajuda:
        'Texto escrito por um programa de inteligência artificial sobre os commits do período: quem fez o ' +
        'quê, por setor e por pessoa, e o que merece conferência. É leitura de apoio — não decide, não ' +
        'imputa ponto e não afirma infração. No mês corrente é refeito uma vez por dia, sobre ontem.',
      botao: 'Gerar o resumo agora',
      refazer: 'Refazer o resumo',
      gerando: 'Gerando o resumo…',
      falhou: 'O resumo não foi gerado',
      estados: {
        PRONTO: 'Resumo atual',
        DESATUALIZADO: 'Entrou commit depois deste resumo',
        PREPARANDO: 'O resumo está sendo preparado',
        SEM_ATIVIDADE: 'Nenhum commit no período: não há o que resumir',
        NUNCA_GERADO: 'O resumo deste mês ainda não foi escrito',
        INDISPONIVEL: 'A leitura automática não está disponível',
        NO_TETO: 'A leitura automática atingiu o limite do mês',
        SEM_CREDITO: 'Os créditos de inteligência artificial deste mês acabaram',
        FALHOU: 'A última tentativa falhou',
      },
      desatualizadoAjuda:
        'O texto abaixo é o último escrito. A próxima rodada diária o refaz sozinha; o botão refaz agora.',
      nuncaGeradoAjuda: 'A rodada diária escreve o resumo de manhã. Quem tem perfil de fiscal pode pedi-lo agora.',
      cobertura: (ate: string, quando: string) => `Cobre até ${ate}. Escrito em ${quando}.`,
      definitivo: 'Resumo definitivo do mês fechado.',
      porSetor: 'Por setor',
      porPessoa: 'Por pessoa',
      pontosDeAtencao: 'Pontos de atenção',
      comoConferir: 'Como conferir:',
      perguntas: 'Perguntas para a fiscalização',
      severidade: { ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa' } as Record<string, string>,
    },
    setores: {
      CONTRATADA: { rotulo: 'DBSeller (contratada)', ajuda: 'A executora do contrato — é ela que o contrato mede.' },
      SMF: { rotulo: 'Secretaria de Fazenda (SMF)', ajuda: 'Órgão da Prefeitura que também desenvolve no e-Cidade.' },
      SEXEC: { rotulo: 'SEXEC / SSTD', ajuda: 'A dona do sistema.' },
      PREFEITURA: { rotulo: 'Prefeitura (outro órgão)', ajuda: 'Conta institucional da Prefeitura sem órgão identificado no e-mail.' },
      SEM_VINCULO: {
        rotulo: 'Sem vínculo identificado',
        ajuda: 'E-mail pessoal (gmail, hotmail…) que o sistema não consegue amarrar a órgão nenhum. Vale corrigir.',
      },
      AUTOMACAO: { rotulo: 'Automação (robô)', ajuda: 'Dependabot, GitHub Actions e afins.' },
    } as Record<string, { rotulo: string; ajuda: string }>,
    setorFixado: 'Setor fixado por uma pessoa',
    setorPelaRegra: 'Setor deduzido do e-mail',
    pessoas: {
      ajuda:
        'Uma linha por pessoa com commit no período, agrupadas por setor. O nome é o que a pessoa configurou ' +
        'no Git — quando não diz quem é, o fiscal pode fixar um nome de exibição.',
      colunas: {
        pessoa: 'Pessoa',
        commits: 'Commits',
        integracoes: 'Integrações',
        dias: 'Dias ativos',
        repositorios: 'Repositórios',
        comSolicitacao: 'Citam solicitação',
        linhas: 'Linhas (+/−)',
        ultimo: 'Último commit',
        areas: 'Áreas',
      },
      maisAreas: (quantas: number) => `+${quantas}`,
      // O "+n" sozinho não diz quais são: o nome delas vai por extenso para leitor de tela e
      // para quem navega por teclado, que não alcança o `title`.
      outrasAreas: (areas: string[]) => `Outras áreas: ${areas.join(', ')}`,
      corrigirSetor: 'Corrigir setor',
      verCommits: 'Ver commits',
      verArquivos: 'Ver arquivos',
      arquivos: {
        titulo: 'Arquivos alterados',
        ajuda: 'Os arquivos que a pessoa tocou no período, do mais alterado para o menos, com o número de commits.',
        nenhum: 'Nenhum arquivo medido para esta pessoa no período.',
        limite: (limite: number) => `Só os ${limite} arquivos mais tocados aparecem aqui.`,
        fechar: 'Fechar',
      },
      dialogo: {
        titulo: 'Corrigir o setor',
        ajuda:
          'A regra deduz o setor do domínio do e-mail. O que você fixar aqui vale daí em diante e a regra ' +
          'não sobrescreve. Fica registrado com o seu nome.',
        setor: 'Setor',
        nomeDeExibicao: 'Nome de exibição',
        nomeDeExibicaoAjuda: 'Como a tela deve chamar esta pessoa. Vazio = o nome que está no commit.',
        observacao: 'Observação',
        observacaoAjuda: 'Por que este setor. Opcional.',
        salvar: 'Salvar',
        cancelar: 'Cancelar',
      },
    },
    commits: {
      ajuda: 'Cada commit da branch principal no período, do mais recente para o mais antigo.',
      filtros: {
        setor: 'Setor',
        todosOsSetores: 'Todos os setores',
        solicitacao: 'Solicitação citada',
        qualquer: 'Todos',
        soSem: 'Só sem solicitação citada',
        soCom: 'Só com solicitação citada',
        tipo: 'Tipo',
        soTrabalho: 'Só trabalho',
        soIntegracao: 'Só integrações',
        busca: 'Buscar',
        buscaAjuda: 'Título, e-mail, SHA ou código da solicitação.',
        incluirHoje: 'Incluir o que chegou hoje',
      },
      colunas: {
        quando: 'Quando',
        quem: 'Quem',
        onde: 'Repositório',
        oQue: 'Mensagem',
        solicitacao: 'Solicitação',
        linhas: 'Linhas',
      },
      integracao: 'integração',
      semSolicitacao: 'sem solicitação citada',
      codigoNaoEncontrado: (codigo: string) => `cita ${codigo}, que não existe neste sistema`,
      chamado: (numero: string) => `chamado ${numero}`,
      verNoGithub: 'Abrir no GitHub',
      arquivos: (n: number) => `${n} ${n === 1 ? 'arquivo' : 'arquivos'}`,
      verArquivos: (n: number) => `arquivos (${n})`,
      esconderArquivos: 'esconder arquivos',
      arquivosAlterados: 'Arquivos alterados neste commit',
      arquivosTruncados: (total: number, mostrados: number) =>
        `O commit alterou ${total} arquivos; a lista guarda só os ${mostrados} primeiros.`,
      semArquivos: 'Os arquivos deste commit ainda não foram lidos do GitHub.',
      total: (total: number) => `${total} ${total === 1 ? 'commit' : 'commits'}`,
      semResultado: { titulo: 'Nenhum commit neste recorte', ajuda: 'Troque os filtros ou o mês.' },
      pagina: (pagina: number, paginas: number) => `Página ${pagina} de ${paginas}`,
      anterior: 'Anterior',
      proxima: 'Próxima',
    },
    repositoriosTab: {
      ajuda: 'Os repositórios com atividade no período e, abaixo, todos os que a coleta conhece.',
      colunas: { repositorio: 'Repositório', commits: 'Commits', integracoes: 'Integrações', autores: 'Pessoas', ultimo: 'Último commit' },
      conhecidos: 'Repositórios conhecidos pela coleta',
      conhecidosAjuda:
        'Descobertos pela instalação do GitHub App na organização. Desligar um repositório o tira das contas ' +
        'sem apagar o que já foi coletado; ligar de novo o devolve.',
      observado: 'Na coleta',
      desligado: 'Fora da coleta',
      arquivado: 'arquivado',
      ligar: 'Ligar na coleta',
      desligar: 'Desligar da coleta',
      escopoPadrao:
        'Por padrão só o e-Cidade entra na coleta: os outros repositórios que o GitHub App alcança nascem ' +
        'fora dela, e o fiscal liga o que quiser acompanhar.',
    },
    autores: {
      titulo: 'Pessoas conhecidas pela coleta',
      ajuda:
        'Todas as pessoas com commit coletado, com o setor e se são da equipe interna. Quem é da equipe ' +
        'interna aparece por último e fica fora de todos os relatórios.',
      colunas: { pessoa: 'Pessoa', setor: 'Setor', equipeInterna: 'Equipe interna', commits: 'Commits' },
      nenhuma: 'Nenhuma pessoa conhecida ainda.',
      naoEh: '—',
      peloDiario: 'pelo Diário de Bordo',
      fixadoPorPessoa: 'fixado por uma pessoa',
      // Quem um fiscal tirou da equipe interna não é igual a quem nunca casou com o Diário: a
      // decisão tem dono e data, e a sincronização não a desfaz. O "—" esconderia isso.
      foraPorPessoa: 'fora da equipe interna por decisão de uma pessoa',
      casadaCom: (nome: string) => `Diário: ${nome}`,
      /**
       * Só os motivos do casamento automático. O backend também grava `PESSOA` como motivo quando
       * um fiscal decide, mas a tela já diz "fixado por uma pessoa" pela origem — repetir seria
       * "decisão de uma pessoa · fixado por uma pessoa".
       */
      motivos: {
        LOGIN: 'mesmo login do GitHub',
        EMAIL: 'mesmo e-mail',
        EMAIL_ALTERNATIVO: 'e-mail alternativo',
        CAIXA_POSTAL: 'mesma caixa postal',
        NOME: 'mesmo nome',
      } as Record<string, string>,
      marcar: 'Marcar como equipe interna',
      desmarcar: 'Tirar da equipe interna',
      dialogo: {
        // Título e ajuda dizem a intenção do botão que abriu o diálogo: quem clicou em "Tirar"
        // precisa ler "tirar", e não um "Equipe interna" neutro com a caixa já marcada.
        marcar: {
          titulo: 'Marcar como equipe interna',
          ajuda:
            'Quem é da equipe interna fica fora de todos os relatórios desta tela. O que você fixar aqui vale ' +
            'daí em diante e a sincronização com o Diário de Bordo não sobrescreve. Fica registrado com o seu nome.',
        },
        tirar: {
          titulo: 'Tirar da equipe interna',
          ajuda:
            'Esta pessoa volta a contar em todos os relatórios desta tela. O que você fixar aqui vale daí em ' +
            'diante e a sincronização com o Diário de Bordo não a marca de novo. Fica registrado com o seu nome.',
        },
        caixa: 'Esta pessoa é da equipe interna (SEXEC)',
        observacao: 'Observação',
        observacaoAjuda: 'Por que esta decisão. Opcional.',
        salvar: 'Salvar',
        cancelar: 'Cancelar',
      },
    },
    coleta: {
      titulo: 'Situação da coleta',
      credencial: 'Credencial',
      ultima: 'Última coleta',
      nenhuma: 'Nenhuma coleta aconteceu ainda.',
      emAndamento: 'Coleta em andamento…',
      coletarAgora: 'Coletar agora',
      coletaPedida: 'Coleta iniciada. Ela roda em segundo plano; esta tela acompanha.',
      jaEmAndamento: 'Já havia uma coleta em andamento.',
      indisponivel: 'A coleta não está disponível.',
      resultado: (repositorios: number, novos: number, detalhados: number) =>
        `${repositorios} ${repositorios === 1 ? 'repositório' : 'repositórios'}, ${novos} ${novos === 1 ? 'commit novo' : 'commits novos'}, ${detalhados} com arquivos e linhas.`,
      falhou: 'A última coleta falhou',
      periodicidade: 'A coleta roda sozinha a cada duas horas; o resumo do dia anterior sai de manhã.',
      diario: {
        titulo: 'Diário de Bordo',
        ajuda: 'De onde vem quem é equipe interna. "Coletar agora" lê o Diário antes de buscar os commits.',
        disponivel: 'Ligado',
        indisponivel: 'Não está disponível.',
        pessoas: (n: number) => `${n} ${n === 1 ? 'pessoa na cópia local' : 'pessoas na cópia local'}`,
        casados: (n: number) => `${n} ${n === 1 ? 'autor casado' : 'autores casados'}`,
        ultima: 'Última leitura',
        nenhuma: 'O Diário ainda não foi lido.',
        emAndamento: 'Leitura em andamento…',
        falhou: 'A última leitura falhou',
        resultado: (pessoas: number, casados: number) =>
          `${pessoas} ${pessoas === 1 ? 'pessoa' : 'pessoas'}, ${casados} ${casados === 1 ? 'autor casado' : 'autores casados'}.`,
      },
    },
    naGmud: {
      titulo: 'Commits que citam esta solicitação',
      ajuda:
        'Commits da branch principal cuja mensagem cita o código desta solicitação. Commit anterior à ' +
        'aprovação é código integrado antes da decisão do comitê — a mesma ressalva da revisão de PR.',
      nenhum: 'Nenhum commit cita esta solicitação até agora.',
      integracao: 'integração',
    },
  },

  /** O cartão da última decisão do comitê, na tela da solicitação. */
  ultimaDecisao: {
    titulo: 'Última decisão do comitê',
    ajuda:
      'O que o comitê decidiu por último sobre esta solicitação, quem decidiu, quando e com que motivo. É a ' +
      'transição já registrada na trilha — mostrada aqui, onde se decide e onde se responde, e não só na ' +
      'aba de histórico.',
    porQuem: (quem: string, quando: string) => `${quem}, em ${quando}`,
    semMotivo: 'Sem motivo registrado.',
  },
  eNiteroi: {
    abrir: 'Abrir no e-Niterói',
    abrirAjuda: 'Abre o chamado na central e-Niterói, numa nova aba, para ler o atendimento antes de julgar.',
  },

  /** A etapa 3 do assistente, quando quem preenche não é quem envia. */
  conferenciaDoCadastro: {
    planoCompleto: (comite: string) =>
      `O plano está completo. Depois de salvar, a solicitação vai aparecer com a ação de envio ao ${comite} disponível.`,
    envioDeOutro:
      'O plano está completo. Você pode salvar o rascunho; o envio ao comitê é feito por quem tem o papel de ' +
      'solicitante — fale com a sua equipe. Preencher tudo e descobrir isso no fim é o que este aviso evita.',
  },

  /** Rótulos que aparecem em mais de uma tela — a razão de existir deste arquivo. */
  tabela: {
    /** Cabeçalho da coluna de botões, só para leitor de tela: o visual já a identifica. */
    acoes: 'Ações',
  },
  limparFiltros: 'Limpar filtros',
  abrirSolicitacao: 'Abrir solicitação',
  voltarParaALista: 'Voltar para a lista',
  enderecoInvalido: 'Endereço inválido',
  minhaConta: 'Minha conta',

  erroGenerico: 'Não foi possível concluir a operação. Tente de novo; se persistir, avise o suporte.',
  loginNaoConfigurado:
    'O login com a conta da Prefeitura ainda não está configurado neste ambiente. Isso é configuração do servidor, não erro seu — avise a equipe da SSTD.',
  motivoObrigatorio: 'Escreva a justificativa. Ela fica registrada na trilha de auditoria com o seu nome.',

  /**
   * Os documentos do contrato (V49): o TR, o contrato, os aditivos — a base da consulta por IA.
   *
   * "Retirar" e não "excluir": o documento não some, sai da consulta. A resposta de ontem citou a
   * página dele, e a citação tem de continuar abrindo.
   */
  documentos: {
    titulo: 'Documentos do contrato',
    descricao:
      'O Termo de Referência, o contrato, os aditivos e o que mais a fiscalização precisar consultar. É daqui ' +
      'que a consulta por IA tira as respostas.',
    enviar: 'Enviar documento',
    abrir: 'Abrir',
    retirar: 'Retirar da consulta',
    vazioTitulo: 'Nenhum documento enviado ainda',
    vazioTexto:
      'Comece pelo Termo de Referência: com ele aqui, qualquer pessoa da Prefeitura pode perguntar sobre o ' +
      'contrato e receber a resposta com a página de onde ela saiu.',
    vazioSemPermissao: 'Quem envia os documentos é a fiscalização. Assim que o primeiro chegar, ele aparece aqui.',
    retirados: 'Retirados da consulta',
    leitura: {
      LIDO: 'Pronto para consulta',
      SEM_TEXTO: 'Sem texto para ler',
      FALHOU: 'Não abriu',
    },
    semTextoAjuda:
      'Este arquivo é imagem (PDF escaneado): ele fica guardado e abre normalmente, mas a consulta não ' +
      'consegue lê-lo. Envie a versão com texto — o SEI exporta assim.',
    paginasComTexto: (comTexto: number, total: number) =>
      comTexto === total ? `${total} páginas lidas` : `Texto lido em ${comTexto} de ${total} páginas`,
    paginasFaltando:
      'Algumas páginas são imagem e ficaram de fora da consulta. Se a resposta que você procura estiver nelas, ' +
      'ela não vai aparecer.',
    envio: {
      titulo: 'Enviar documento do contrato',
      descricao: 'PDF, DOCX ou texto, até 25 MB. O texto é lido na hora — um TR de 200 páginas leva alguns segundos.',
      arquivo: 'Arquivo',
      nome: 'Nome do documento',
      nomeAjuda: 'É o nome que aparece na lista e nas citações das respostas. Ex.: "Termo de Referência".',
      tipo: 'Que documento é',
      enviando: 'Enviando e lendo o texto…',
      confirmar: 'Enviar',
    },
    retirada: {
      titulo: 'Retirar da consulta',
      descricao:
        'O documento sai das respostas, mas continua na lista e abrindo — as respostas antigas citam as páginas ' +
        'dele. Diga por que ele sai.',
      motivo: 'Motivo',
      motivoAjuda: 'Ex.: "substituído pela versão assinada", "enviado por engano".',
      confirmar: 'Retirar',
    },
  },

  /**
   * "Pergunte ao contrato": a consulta por IA aos documentos (V50).
   *
   * O tom é de apoio de leitura, nunca de parecer: a resposta diz o que o documento estabelece e
   * aponta a página; quem decide é o fiscal. Por isso o aviso de rodapé existe em toda resposta.
   */
  consulta: {
    titulo: 'Pergunte ao contrato',
    descricao:
      'Respostas tiradas do Termo de Referência e dos outros documentos do contrato, sempre com a página de ' +
      'onde saíram.',
    lendo: (quantos: number) => (quantos === 1 ? 'Lendo 1 documento' : `Lendo ${quantos} documentos`),
    novaConversa: 'Nova conversa',
    suasConversas: 'Suas conversas',
    semConversas: 'As perguntas que você fizer ficam guardadas aqui. Só você as vê.',
    boasVindas: 'O que você quer saber do contrato?',
    boasVindasAjuda:
      'Pergunte do jeito que perguntaria a um colega. A resposta usa só o que está escrito nos documentos — e ' +
      'diz quando não encontrar.',
    sugestoes: [
      'Quais são os prazos de atendimento dos chamados?',
      'Como é calculado o IAP?',
      'O que acontece se a contratada implantar uma mudança sem aprovação do comitê?',
      'Quais relatórios a contratada precisa entregar todo mês?',
    ],
    campo: 'Sua pergunta',
    placeholder: 'Ex.: o que o TR diz sobre o plano de volta de uma mudança?',
    enviar: 'Perguntar',
    enviarAjuda: 'Enter envia · Shift+Enter quebra a linha',
    pensando: 'Procurando nos documentos e escrevendo a resposta. Pode levar até um minuto.',
    fontes: 'Referências',
    abrirNaPagina: (pagina?: number) => (pagina ? `Abrir na página ${pagina}` : 'Abrir o documento'),
    pagina: (pagina?: number) => (pagina ? `p. ${pagina}` : 'sem página'),
    aviso:
      'Resposta gerada por IA como apoio de leitura. O que vale é o texto do documento: confira a página ' +
      'citada antes de usar numa decisão.',
    semFonte:
      'Esta resposta veio sem referência a um trecho dos documentos. Não a use sem conferir — abra o ' +
      'Termo de Referência e procure o assunto.',
    naoEncontrada: 'Não encontrado nos documentos',
    foraDoEscopo: 'Fora do assunto do contrato',
    falhou: 'A resposta não foi concluída',
    perguntarDeNovo: 'Perguntar de novo',
    indisponivelTitulo: 'A consulta não está disponível agora',
    semDocumentosTitulo: 'Ainda não há documento para consultar',
    semDocumentosTexto:
      'A consulta lê os documentos do contrato. Assim que a fiscalização enviar o Termo de Referência, você ' +
      'já pode perguntar.',
    irParaDocumentos: 'Ir para os documentos',
  },

  /**
   * A identificação institucional do rodapé, em toda tela.
   *
   * Mora aqui e não no `Layout` porque aparece em dois lugares — a moldura das telas logadas e o
   * painel da tela de entrada —, e a Regra nº 4 existe justamente para o dia em que a Subsecretaria
   * mudar de nome: é uma string, não uma varredura.
   *
   * O nome segue o que os outros sistemas da casa já estampam (GESTAO-PESSOAS, FERIAS,
   * DIARIO-BORDO). Atenção: a seed `V9__seed_desenvolvimento.sql` deste repositório grava
   * "Subsecretaria de Ciência, Tecnologia e Transformação Digital", que não bate com nenhum deles —
   * divergência conhecida, a corrigir por migration nova (migration é histórico, não se edita).
   */
  /**
   * O nome do sistema, como a pessoa o lê no menu, na aba do navegador e no login.
   *
   * Era "Sistema Gestão", que é também o nome do sistema ANTERIOR (sistemagestao.niteroi.rj.gov.br)
   * — duas coisas com o mesmo nome no mesmo processo. Decisão do dono em 26/10/2026. Um lugar só:
   * as menções ao sistema antigo continuam como estão, e os nomes internos de código também.
   */
  nomeDoSistema: 'Fiscalização',
  orgao: 'Secretaria Executiva - Subsecretaria de Transformação Digital',
} as const
